<template>
  <div
    v-if="shouldRender"
    class="message-item"
    :class="messageClass"
    :data-msg-id="id"
    :data-turn-key="type === 'user' ? 'turn-' + id : undefined"
  >
    <div v-if="isQuotaExhausted" class="quota-exhausted-card">
      <div class="quota-exhausted-head">
        <span class="quota-exhausted-title">{{quotaInfo.message}}</span>
      </div>
      <!-- <div class="quota-exhausted-desc">{{ quotaInfo.message || '账户额度耗尽，本次对话无法发送' }}</div> -->
      <div class="quota-exhausted-stats">
        <div class="quota-stat">
          <span class="quota-stat-label">已用积分</span>
          <span class="quota-stat-value">{{ formatPoints(quotaInfo.usedPoints) }}</span>
        </div>
        <div class="quota-stat">
          <span class="quota-stat-label">剩余积分</span>
          <span class="quota-stat-value">{{ formatPoints(quotaInfo.remainingPoints) }}</span>
        </div>
        <div class="quota-stat">
          <span class="quota-stat-label">总积分</span>
          <span class="quota-stat-value">{{ formatPoints(quotaInfo.totalPoints) }}</span>
        </div>
      </div>
      <div class="quota-exhausted-footer">
        <button type="button" class="quota-exhausted-btn" @click="handleQuotaAction">申请配额</button>
      </div>
    </div>
    <div v-else-if="isSeatLimit" class="seat-limit-card">
      <div class="seat-limit-head">
        <i class="el-icon-warning-outline seat-limit-icon"></i>
        <span class="seat-limit-title">企业用户席位已达上限</span>
      </div>
      <div class="seat-limit-desc">当前企业用户席位已满 ({{ seatLimitInfo.used }}/{{ seatLimitInfo.total }})，本次对话暂时无法使用该服务。</div>
      <div class="seat-limit-stats">
        <div class="seat-stat">
          <span class="seat-stat-label">当前席位</span>
          <span class="seat-stat-value">{{ seatLimitInfo.used }}</span>
        </div>
        <div class="seat-stat">
          <span class="seat-stat-label">可用席位</span>
          <span class="seat-stat-value">{{ seatLimitInfo.available }}</span>
        </div>
        <div class="seat-stat">
          <span class="seat-stat-label">席位上限</span>
          <span class="seat-stat-value">{{ seatLimitInfo.total }}</span>
        </div>
      </div>
      <div class="seat-limit-footer">建议联系租户管理员升级套餐、增加席位，开通后即可继续对话。</div>
    </div>
    <div v-else class="message-wrapper" :class="{ 'is-user': type === 'user', 'has-wide-component': hasWideComponent }">
      <div class="message-bubble" :class="{ 'has-followups': type === 'user' && hasFollowUps }">
        <!-- User message -->
        <template v-if="type === 'user'">
          <div v-if="attachments && attachments.length > 0" class="message-attachments">
            <!-- 图片附件 -->
            <div v-if="imageAttachments.length > 0" class="attachment-images">
              <div v-for="(file, idx) in imageAttachments" :key="'img-' + idx" class="attachment-image-item">
                <img :src="file.url" :alt="file.name" @click="openImagePreview(file.url, file.name)" />
              </div>
            </div>
            <!-- 文件附件 -->
            <div v-if="fileAttachments.length > 0" class="attachment-files">
              <div v-for="(file, idx) in fileAttachments" :key="'file-' + idx" class="attachment-file-item" @click="downloadAttachment(file)">
                <div class="attachment-type-badge" :style="{ backgroundColor: getFileTypeColor(file.name) }">
                  <span>{{ getFileTypeLabel(file.name) }}</span>
                </div>
                <div class="attachment-file-name" :title="file.name">{{ file.name || $t('workbench.attachment') }}</div>
              </div>
            </div>
          </div>
          <div v-if="richHtml && content" class="rich-html-content" v-html="content"></div>
          <!-- 追问消息：正文气泡 + 「追问」引用卡片 -->
          <div v-else-if="hasFollowUps" class="user-followup-wrap">
            <div v-if="followUpBody" class="followup-body-bubble">
              <div
                ref="userTextContent"
                class="user-text-content"
                :class="{ 'is-collapsed': isUserTextCollapsed }"
                v-html="renderFollowUpBodyHtml"
              ></div>
              <button
                v-if="userTextOverflow && !userTextExpanded"
                type="button"
                class="user-text-toggle"
                @click="expandUserText"
              >展开全部</button>
            </div>
            <div class="followup-block">
              <div class="followup-block-title">
                <img :src="followUpIcon" alt="" aria-hidden="true" class="followup-block-icon" />
                <span>追问</span>
              </div>
              <div
                v-for="item in followUpItems"
                :key="'followup-' + item.index"
                class="followup-card"
              >
                <div class="followup-card-quote">
                  <p
                    class="followup-card-quote-text"
                    :class="{ 'is-expanded': isFollowUpQuoteExpanded(item.index) }"
                    :data-followup-quote="item.index"
                  >{{ item.quote }}</p>
                  <button
                    v-if="hasFollowUpQuoteOverflow(item.index)"
                    type="button"
                    class="followup-card-quote-arrow"
                    :class="{ 'is-expanded': isFollowUpQuoteExpanded(item.index) }"
                    :title="isFollowUpQuoteExpanded(item.index) ? '收起' : '展开'"
                    :aria-expanded="isFollowUpQuoteExpanded(item.index) ? 'true' : 'false'"
                    @click.stop="toggleFollowUpQuote(item.index)"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 18l6-6-6-6"></path>
                    </svg>
                  </button>
                </div>
                <div v-if="item.note" class="followup-card-note">
                  <p
                    class="followup-card-note-text"
                    :class="{
                      'is-expanded': isFollowUpNoteExpanded(item.index),
                      'is-collapsed': hasFollowUpNoteOverflow(item.index) && !isFollowUpNoteExpanded(item.index)
                    }"
                    :data-followup-note="item.index"
                  >{{ item.note }}</p>
                  <button
                    v-if="hasFollowUpNoteOverflow(item.index) && !isFollowUpNoteExpanded(item.index)"
                    type="button"
                    class="user-text-toggle"
                    @click.stop="expandFollowUpNote(item.index)"
                  >展开全部</button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="questioningRef" class="message-text">
              <div class="questioning-quote">
                <div
                  class="quote-content"
                  :class="{ 'is-collapsed': !quoteExpanded }"
                  ref="quoteContent"
                >{{ questioningRef }}</div>
                <div
                  v-if="quoteOverflow"
                  class="quote-toggle"
                  @click="toggleQuote"
                >
                  {{ quoteExpanded ? '收起' : '展开' }}
                  <i
                    class="el-icon-arrow-down"
                    :style="{ transform: quoteExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }"
                  ></i>
                </div>
              </div>
              <div class="questioning-text" style="margin-top:8px;" v-html="userContentHtml"></div>
            </div>
          <div v-else-if="displayContent" class="message-text message-text--collapsible">
            <div
              ref="userTextContent"
              class="user-text-content"
              :class="{ 'is-collapsed': isUserTextCollapsed }"
              v-html="userContentHtml"
            ></div>
            <button
              v-if="userTextOverflow && !userTextExpanded"
              type="button"
              class="user-text-toggle"
              @click="expandUserText"
            >展开全部</button>
          </div>
        </template>
        <!-- Assistant message -->
        <template v-else-if="type === 'assistant'">
          <!-- SOP flow messages: full-width card takeover -->
          <SopSelect v-if="category === 'sop_select'" :data="msgData" :info="sopInfo" :type="sopType" />
          <SopPanne v-else-if="category === 'sop_1'" :data="msgData" :info="sopInfo" :type="sopType" />
          <SopSecond v-else-if="category === 'sop_2'" :data="msgItems.length ? msgItems : msgData" :info="sopInfo" :type="sopType" />
          <SopCallback v-else-if="category === 'sop_report'" :data="msgData" :info="sopInfo" />
          <SopReport v-else-if="category === 'sop_report_message'" :data="msgData" :info="sopInfo" :message="historyData || ''" />
          <!-- Normal rendering -->
          <template v-else>
            <div v-if="richHtml && content" class="rich-html-content" v-html="content"></div>
            <div v-else-if="isError" class="error-banner">
              <i class="el-icon-circle-close"></i>
              {{ content || $t('workbench.requestError') }}
            </div>
            <div v-else class="markdown-body-wrap">
              <div
                ref="markdownRoot"
                class="markdown-body"
                @click="handleMarkdownClick"
                @mousedown="handleSelectionPointerDown"
              >
              <template v-for="seg in visibleSegments">
                <!-- WS card with sop_select → SopSelect -->
                <SopSelect
                  v-if="seg.type === 'component' && seg.tagName === 'card' && seg.props && seg.props.type === 'sop_select'"
                  :key="'sop-card-' + seg._stableKey"
                  :query="(seg.props && seg.props.query) || ''"
                  :info="toSopCardInfo(seg)"
                  type="active"
                />
                <div v-else-if="seg.type === 'text'" :key="'t-' + seg._stableKey" v-html="renderSegmentText(seg)"></div>
                <div v-else-if="seg.type === 'component-loading'" :key="'cl-' + seg._stableKey" class="component-loading-placeholder">
                  <span class="loading-text">{{ seg.loadingText }}{{ $t('workbench.pleaseWait') }}</span>
                  <i class="el-icon-loading loading-icon"></i>
                </div>
                <div v-else-if="seg.type === 'component-checking'" :key="'cc-' + seg._stableKey" class="component-loading-placeholder">
                  <span class="loading-text">{{ $t('workbench.componentChecking') }}{{ $t('workbench.pleaseWait') }}</span>
                  <i class="el-icon-loading loading-icon"></i>
                </div>
                <MdRenderFallback
                  v-else-if="seg.type === 'component-invalid'"
                  :key="'cinv-' + seg._stableKey"
                  :reason="seg.errorCode || 'json.syntax'"
                  :component-name="seg.componentName || ''"
                  :raw="seg.raw || ''"
                  @fixed="handleComponentFixed(seg, $event)"
                />
                <MediaFileCard v-else-if="seg.type === 'media-file'" :key="'mf-' + seg._stableKey" :source="seg.source" />
                <CardList
                  v-else-if="seg.tagName === 'card' && seg.props && seg.props.type === 'cardList'"
                  :key="'cardlist-' + seg._stableKey"
                  :data="seg.props"
                />
                <MermaidViewer
                  v-else-if="seg.type === 'mermaid'"
                  :key="'m-' + seg._stableKey"
                  :code="seg.code"
                  :message-id="id"
                />
                <MdCard
                  v-else-if="seg.tagName === 'card'"
                  :key="'c-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                  :is-history="isHistory"
                  :message-id="id"
                  @card-action="handleCardAction"
                  @loading-done="handleLoadingDone(seg._stableKey, $event)"
                />
                <CoreDashboard
                  v-else-if="seg.tagName === 'core-dashboard'"
                  :key="'cd-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <AnalysisSummary
                  v-else-if="seg.tagName === 'analysis-summary'"
                  :key="'as-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <KeyJudgment
                  v-else-if="seg.tagName === 'key-judgment'"
                  :key="'kj-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <DecisionSuggestion
                  v-else-if="seg.tagName === 'decision-suggestion'"
                  :key="'ds-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <AnalysisConclusion
                  v-else-if="seg.tagName === 'analysis-conclusion'"
                  :key="'ac-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <KeyFinding
                  v-else-if="seg.tagName === 'key-finding'"
                  :key="'kf-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <StrengthsWeaknesses
                  v-else-if="seg.tagName === 'strengths-weaknesses'"
                  :key="'sw-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <MetricCompare
                  v-else-if="seg.tagName === 'metric-compare'"
                  :key="'mc-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <MetricAnalysis
                  v-else-if="seg.tagName === 'metric-analysis'"
                  :key="'ma-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <SectionTitle
                  v-else-if="seg.tagName === 'section-title'"
                  :key="'st-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <SectionBlock
                  v-else-if="seg.tagName === 'section-block'"
                  :key="'sb-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <ChatCardCreateTaskComp
                  v-else-if="seg.tagName === 'chat-card-create-task'"
                  :key="'ccct-' + seg._stableKey"
                  v-bind="parsePropsChatCard(seg.props, seg._stableKey)"
                />
                <MdChart
                  v-else-if="seg.tagName === 'chart'"
                  :key="'ch-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                  :source-json="seg.raw || ''"
                />
                <MdSheet
                  v-else-if="seg.tagName === 'sheet'"
                  :key="'s-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <MdTimelineLog
                  v-else-if="seg.tagName === 'timeline-log'"
                  :key="'tl-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <MdResultSummary
                  v-else-if="seg.tagName === 'result-summary'"
                  :key="'rs-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <PageList
                  v-else-if="seg.tagName === 'pagelist'"
                  :key="'pl-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                  :is-history="isHistory"
                  :message-id="id"
              />
                <MdDrawer
                  v-else-if="seg.tagName === 'drawer'"
                  :key="'dr-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                  :is-history="isHistory"
                  :message-id="id"
                  @drawer-action="handleDrawerAction"
                />
                <MdRecommendation
                  v-else-if="seg.tagName === 'recommendation'"
                  :key="'rc-' + seg._stableKey"
                  v-bind="parseProps(seg.props, seg._stableKey)"
                />
                <div
                  v-else-if="seg.tagName === 'center-iframe'"
                  :key="'ci-' + seg._stableKey"
                  class="center-iframe-btn"
                  @click="handleCenterIframeClick(seg.props)"
                >
                  <i class="el-icon-full-screen"></i>
                  <span>{{ getCenterIframeName(seg.props) }}</span>
                  <i class="el-icon-arrow-right"></i>
                </div>
                <!-- Fallback: friendly notice for unrecognized / unrenderable component blocks -->
                <MdRenderFallback
                  v-else-if="seg.type === 'component'"
                  :key="'raw-' + seg._stableKey"
                  :reason="seg.errorCode || 'component.unsupported'"
                  :component-name="seg.componentName || seg.tagName || ''"
                  :raw="seg.raw || ''"
                  @fixed="handleComponentFixed(seg, $event)"
                />
              </template>
              </div>
              <!-- 追问高亮色块 -->
              <div
                v-if="annotationRects.length"
                class="annotation-overlay"
                data-ca-annotation-overlay
                aria-hidden="true"
              >
                <div
                  v-for="rect in annotationRects"
                  :key="rect.key"
                  class="annotation-rect"
                  :style="annotationRectStyle(rect)"
                ></div>
              </div>
              <!-- 追问序号角标 -->
              <div
                v-if="annotationChips.length"
                class="annotation-overlay annotation-overlay--chips"
                data-ca-annotation-overlay
              >
                <button
                  v-for="chip in annotationChips"
                  :key="chip.key"
                  type="button"
                  class="annotation-chip"
                  :style="annotationChipStyle(chip)"
                  @click.stop="handleAnnotationChipClick(chip)"
                >
                  {{ chip.index }}
                </button>
              </div>
            </div>
            <div v-if="isStreamingActive && !isQuotaExhausted" class="streaming-cursor"></div>
            <!-- 回复底部操作行：复制 / Markdown -->
            <div v-if="showTurnActions" class="turn-actions">
              <div class="turn-action-group">
                <button
                  type="button"
                  class="turn-action-btn"
                  :class="{ 'is-success': copied }"
                  @click="handleCopyMessage"
                >
                  <i :class="copied ? 'el-icon-check' : 'el-icon-document-copy'"></i>
                  <span>{{ copied ? '已复制!' : '复制' }}</span>
                </button>
                <button type="button" class="turn-action-btn" @click="openMarkdownDialog">
                  <i class="el-icon-document"></i>
                  <span>Markdown</span>
                </button>
              </div>
            </div>
          </template>
        </template>
        <template v-else-if="type === 'tool' || type === 'toolCall'">
          <div class="tool-content">
            <i class="el-icon-setting"></i>
            <span>{{ content }}</span>
            <span v-if="toolName" class="tool-name">{{ toolName }}</span>
          </div>
          <div v-if="result" class="tool-result">
            <pre>{{ result }}</pre>
          </div>
        </template>
      </div>
      <div class="message-time">{{ formatTime(timestamp) }}</div>
    </div>

    <!-- 图片预览遮罩层 -->
    <transition name="image-preview-fade">
      <div v-if="imagePreviewVisible" class="image-preview-overlay" @click.self="imagePreviewVisible = false">
        <div class="image-preview-container">
          <div class="image-preview-header">
            <span class="image-preview-title">{{ imagePreviewName }}</span>
            <div class="image-preview-actions">
              <button class="preview-action-btn" @click="downloadPreviewImage" :title="$t('workbench.download') || '下载'">
                <i class="el-icon-download"></i>
              </button>
              <button class="preview-action-btn" @click="imagePreviewVisible = false" :title="$t('workbench.close') || '关闭'">
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
          <div class="image-preview-content">
            <img :src="imagePreviewUrl" :alt="imagePreviewName" @click.stop />
          </div>
        </div>
      </div>
    </transition>
    <!-- Markdown 原文弹层（对齐客户端 CodePreviewOverlay 全屏代码预览） -->
    <div ref="markdownOverlayHolder">
      <transition name="markdown-source-fade">
        <div v-if="markdownDialogVisible" class="markdown-source-overlay">
          <div class="markdown-source-header">
            <div class="markdown-source-header-side"></div>
            <div class="markdown-source-header-center">
              <span class="markdown-source-badge">
                <i class="el-icon-reading"></i>
                <span>读取</span>
              </span>
              <span
                class="markdown-source-badge markdown-source-badge--file"
                title="response.md"
              >response.md</span>
            </div>
            <div class="markdown-source-header-side markdown-source-header-side--right">
              <button
                type="button"
                class="markdown-source-icon-btn markdown-source-copy-btn"
                :title="markdownCopied ? '已复制' : '复制'"
                @click="handleCopyMarkdownSource"
              >
                <i :class="markdownCopied ? 'el-icon-check' : 'el-icon-document-copy'"></i>
              </button>
              <button
                type="button"
                class="markdown-source-icon-btn"
                title="关闭 (Esc)"
                @click="closeMarkdownDialog"
              >
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
          <div class="markdown-source-scroll">
            <div class="markdown-source-scroll-inner">
              <div class="markdown-source-card">
                <div class="markdown-source-card-title">代码</div>
                <div class="markdown-source-code">
                  <div class="markdown-source-gutter">
                    <div
                      v-for="lineNo in markdownSourceLineCount"
                      :key="lineNo"
                      class="markdown-source-line-no"
                    >{{ lineNo }}</div>
                  </div>
                  <div class="markdown-source-code-body">
                    <pre class="markdown-source-pre"><code>{{ markdownSource }}</code></pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <trial-apply-dialog v-if="isQuotaExhausted" v-model="trialApplyVisible" :source="6" />
  </div>
</template>

<script>
import { marked } from 'marked'
import { renderUserTextWithAttachments } from '@/utils/markdownRenderer'
import { extractJsonCodeBlocks, generateComponentKey, COMPONENT_TYPE_NAMES, reparseComponentJson, diagnoseComponentJson, findTruncatedJsonObjectEnd, findBalancedJsonObject as findBalancedCardEnd } from './md-renderer'
import { stripProcessMarker } from '../utils/processTrace'
import MdCard from './md-components/MdCard.vue'
import MdChart from './md-components/MdChart.vue'
import MdSheet from './md-components/MdSheet.vue'
import MdTimelineLog from './md-components/MdTimelineLog.vue'
import MdResultSummary from './md-components/MdResultSummary.vue'
import MdDrawer from './md-components/MdDrawer.vue'
import MdRecommendation from './md-components/MdRecommendation.vue'
import PageList from './md-components/PageList.vue'
import CoreDashboard from './md-components/CoreDashboard.vue'
import AnalysisSummary from './md-components/AnalysisSummary.vue'
import KeyJudgment from './md-components/KeyJudgment.vue'
import DecisionSuggestion from './md-components/DecisionSuggestion.vue'
import AnalysisConclusion from './md-components/AnalysisConclusion.vue'
import KeyFinding from './md-components/KeyFinding.vue'
import StrengthsWeaknesses from './md-components/StrengthsWeaknesses.vue'
import MetricCompare from './md-components/MetricCompare.vue'
import MetricAnalysis from './md-components/MetricAnalysis.vue'
import SectionTitle from './md-components/SectionTitle.vue'
import SectionBlock from './md-components/SectionBlock.vue'
import ChatCardCreateTaskComp from '../../workbench-new/components/ChatCardCreateTask.vue'
import SopSelect from './md-components/Sop/SopSelect.vue'
import SopPanne from './md-components/Sop/SopPanne.vue'
import SopSecond from './md-components/Sop/SopSecond.vue'
import SopCallback from './md-components/Sop/SopCallback.vue'
import SopReport from './md-components/Sop/SopReport.vue'
import MediaFileCard from './MediaFileCard.vue'
import CardList from './md-components/CardList.vue'
import MdRenderFallback from './md-components/MdRenderFallback.vue'
import MermaidViewer from './MermaidViewer.vue'
import TrialApplyDialog from '@/components/common/TrialApplyDialog'
import followUpIcon from '@/assets/imgs/ai/follow-up.png'
import { followUpStore } from '../followUp/store'
import {
  collectTextSegments,
  resolveNodeOffset,
  clamp,
  SELECTION_POINTER_MAX_AGE_MS
} from '../followUp/annotation-core'
import {
  splitFollowUpsSection,
  resolveTextAnnotations,
  computeAnnotationOverlayGeometry,
  getAnnotationChipVisual,
  getAnnotationRectVisual
} from '../followUp/follow-ups'

marked.setOptions({
  gfm: true,
  breaks: true
})

// 覆盖默认的 del tokenizer，使只有 ~~text~~ 才渲染为删除线
// 防止单 ~ 用于日期范围（如 5月3日~5月16日）时被误渲染
marked.use({
  tokenizer: {
    del(src) {
      const cap = /^~~([^~]+?)~~/.exec(src)
      if (cap) {
        return {
          type: 'del',
          raw: cap[0],
          text: cap[1],
          tokens: this.lexer.inlineTokens(cap[1])
        }
      }
      return undefined
    }
  }
})

const SILENT_PREFIXES_LIST = ['[iframe-response]', '[card-action]', '[form-action]', '[table-action]', '[dataset-action]', '[skill-action]', '[cardlist-action]', '[drawer-action]', '[report-optimize]', '[questioning-action]', '[mock:']

/**
 * 把一段 markdown 文本切成「渐显单元」，用于闸门放行后模拟流式输出。
 * 每个单元按原文顺序拼接后与原文完全一致（丢掉首部空行），因此任意前缀都是合法 markdown。
 * 切分点：代码块整体、表格整体、列表整体、引用整体；其余以段落/单个 HTML 标签行为单位。
 */
function splitRevealUnits(text) {
  if (!text) return []
  const lines = String(text).match(/[^\n]*\n|[^\n]+$/g) || []
  const units = []
  let current = ''
  let inFence = false
  let prevKind = ''
  const flush = () => {
    if (current) {
      units.push(current)
      current = ''
    }
  }
  const classify = (line) => {
    if (!line.trim()) return 'blank'
    if (/^\s*```/.test(line)) return 'fence'
    if (/^\s*<!--\s*md-table:full\s*-->\s*$/.test(line)) return 'table-marker'
    if (/^\s*\|/.test(line)) return 'table'
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) return 'list'
    if (/^\s*>/.test(line)) return 'quote'
    if (/^\s*<[^>]+>\s*$/.test(line)) return 'html'
    if (/^\s*#{1,6}\s/.test(line)) return 'heading'
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) return 'hr'
    return 'text'
  }
  for (const line of lines) {
    if (line === '') continue
    const kind = classify(line.replace(/\r?\n$/, ''))
    if (kind === 'blank') {
      // 空行附着在前一个单元尾部（保持原文前缀合法）；文本开头的空行直接丢弃
      if (!current) continue
      current += line
      prevKind = kind
      continue
    }
    let join = false
    if (inFence) {
      join = true
    } else if (kind === 'table') {
      join = prevKind === 'table' || prevKind === 'table-marker'
    } else if (kind === 'list') {
      join = prevKind === 'list'
    } else if (kind === 'quote') {
      join = prevKind === 'quote'
    } else if (kind === 'text') {
      join = prevKind === 'text'
    }
    if (!join) flush()
    current += line
    if (kind === 'fence') inFence = !inFence
    prevKind = kind
    if (kind === 'html' || kind === 'heading' || kind === 'hr') flush()
  }
  flush()
  return units
}

/**
 * 从消息内容中提取“额度用尽”JSON 信息。
 * 兼容：纯 JSON 全文、websocket 错误前缀 `**[错误]** {...}`、markdown 代码块包裹等场景。
 */
function extractQuotaInfo(content) {
  if (!content || typeof content !== 'string') return null
  let text = content.trim()
  // 去掉 websocket 错误消息统一加的前缀
  text = text.replace(/^\*\*\[错误\]\*\*\s*/, '').replace(/^\[错误\]\s*/, '')
  // 去掉 markdown 代码块包裹
  const fence = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/)
  if (fence) text = fence[1].trim()
  // 提取内容中的 JSON 对象
  const jsonMatch = text.match(/\{[\s\S]*\}/)
  let obj = null
  if (jsonMatch) {
    try { obj = JSON.parse(jsonMatch[0]) } catch (e) { obj = null }
  }
  if (!obj) {
    try { obj = JSON.parse(text) } catch (e) { obj = null }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
  const message = String(obj.message || '')
  const isQuota = obj.exhausted === true || (obj.canSend === false && /额度|积分|exhausted/i.test(message))
  if (!isQuota) return null
  return {
    exhausted: !!obj.exhausted,
    canSend: obj.canSend !== false,
    totalPoints: obj.totalPoints,
    usedPoints: obj.usedPoints,
    remainingPoints: obj.remainingPoints,
    message
  }
}

/**
 * 从消息内容中提取“席位上限”JSON 信息（前端在席位校验未通过时主动生成的 assistant 消息）
 * 结构：{ seatLimit: true, used, available, total, message }
 */
function extractSeatLimitInfo(content) {
  if (!content || typeof content !== 'string') return null
  const text = content.trim()
  const jsonMatch = text.match(/\{[\s\S]*\}/)
  let obj = null
  if (jsonMatch) {
    try { obj = JSON.parse(jsonMatch[0]) } catch (e) { obj = null }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null
  if (obj.seatLimit !== true) return null
  return {
    used: Number(obj.used) || 0,
    available: Number(obj.available) || 0,
    total: Number(obj.total) || 0,
    message: String(obj.message || '')
  }
}

export default {
  name: 'CosmoClawMessageItem',
  components: { TrialApplyDialog, MdCard, MdChart, MdSheet, MdTimelineLog, MdResultSummary, MdDrawer, MdRecommendation, PageList, SopSelect, SopPanne, SopSecond, SopCallback, SopReport, MediaFileCard, CardList, MdRenderFallback, MermaidViewer, CoreDashboard, AnalysisSummary, KeyJudgment, DecisionSuggestion, AnalysisConclusion, KeyFinding, StrengthsWeaknesses, MetricCompare, MetricAnalysis, SectionTitle, SectionBlock, ChatCardCreateTaskComp },
  props: {
    id: [String, Number],
    type: {
      type: String,
      validator: v => ['user', 'assistant', 'system', 'tool', 'thinking', 'toolCall'].includes(v)
    },
    content: { type: String, default: '' },
    attachments: { type: Array, default: () => [] },
    timestamp: { type: Number, default: 0 },
    isStreaming: { type: Boolean, default: false },
    isHistory: { type: Boolean, default: false },
    isError: { type: Boolean, default: false },
    toolName: { type: String, default: '' },
    toolCallId: { type: String, default: '' },
    phase: { type: String, default: '' },
    args: { type: String, default: '' },
    result: { type: String, default: '' },
    category: { type: String, default: '' },
    msgData: { type: [Object, Array], default: () => ({}) },
    msgItems: { type: [Array, Object], default: () => [] },
    historyData: { type: [String, Array], default: '' },
    msgParent: { type: [Object, Array], default: null },
    mediaUrls: { type: Array, default: () => [] },
    richHtml: { type: Boolean, default: false },
    // 宽屏形态(抽屉放大态 / 数字人会话页),使 hasWideComponent 返回 true
    wideToolCard: { type: Boolean, default: false }
  },
  data() {
    return {
      imagePreviewVisible: false,
      trialApplyVisible: false,
      imagePreviewUrl: '',
      imagePreviewName: '',
      jsonCardCheckMap: {}, // JSON 卡片校验结果缓存：checkKey -> {status, valid, cardJson, payload, errorMessage}
      // 兜底修复结果缓存：key 优先 checkKey（含块序号，可区分同文不同块），无 checkKey 时用 'raw:' + 原文
      jsonCardFixedMap: {},
      holdDoneKeys: {}, // 已放行的 loading 卡片（holdContent）稳定 key 集合：key -> true
      // 闸门放行后的逐段渐显（模拟流式输出）
      replay: { active: false, gateKey: '', segIndex: 0, unitIndex: 0 },
      replayTimer: null,
      // 追问引用展开/收起
      quoteExpanded: false,
      quoteOverflow: false, // 内容是否超过 4 行（溢出时才显示展开按钮）
      // 用户气泡正文展开/收起（超过 4 行折叠）
      userTextExpanded: false,
      userTextOverflow: false,
      // 追问卡片引用文本展开/收起（按条目序号 key）
      followUpQuoteExpanded: {},
      followUpQuoteOverflow: {},
      followUpNoteExpanded: {},
      followUpNoteOverflow: {},
      // 复制 / Markdown 原文
      copied: false,
      copyTimer: null,
      markdownDialogVisible: false,
      markdownCopied: false,
      markdownCopiedTimer: null,
      // 追问高亮 overlay（相对 markdown 根节点）
      annotationRects: [],
      annotationChips: [],
      followUpIcon
    }
  },
  created() {
    // 非响应式的内部状态（避免放入 data 触发 vue/no-reserved-keys）
    this._overlayFrame = null
    this._overlaySig = ''
    this._selectionPointer = null
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscKey)
    document.addEventListener('mouseup', this.handleDocumentMouseUp)
    window.addEventListener('resize', this.scheduleAnnotationOverlay)
    // Markdown 原文弹层需要脱离消息列表滚动/变换上下文，挂到 body 上
    if (this.$refs.markdownOverlayHolder) {
      document.body.appendChild(this.$refs.markdownOverlayHolder)
    }
    this.$nextTick(() => {
      this.checkQuoteOverflow()
      this.checkFollowUpQuoteOverflow()
      this.checkFollowUpNoteOverflow()
      this.checkUserTextOverflow()
      this.refreshAnnotationOverlay()
    })
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscKey)
    document.removeEventListener('mouseup', this.handleDocumentMouseUp)
    window.removeEventListener('resize', this.scheduleAnnotationOverlay)
    document.removeEventListener('keydown', this.handleMarkdownOverlayKeydown, true)
    this._unlockBodyScroll()
    if (this.$refs.markdownOverlayHolder && this.$refs.markdownOverlayHolder.parentNode) {
      this.$refs.markdownOverlayHolder.parentNode.removeChild(this.$refs.markdownOverlayHolder)
    }
    if (this.copyTimer) clearTimeout(this.copyTimer)
    if (this.markdownCopiedTimer) clearTimeout(this.markdownCopiedTimer)
    if (this._overlayFrame) cancelAnimationFrame(this._overlayFrame)
    this._stopReplay()
  },
  computed: {
    shouldRender() {
      if (this.type === 'thinking') return false
      if (this.isQuotaExhausted) return true
      if (this.isSeatLimit) return true
      // User messages with attachments but no text (file/image only) still render;
      // user visibility is decided below by attachments/richHtml, so do not filter here.
      if (
        !this.content &&
        this.type !== 'user' &&
        this.type !== 'tool' &&
        this.type !== 'toolCall' &&
        (!this.mediaUrls || !this.mediaUrls.length)
      ) return false
      for (const prefix of SILENT_PREFIXES_LIST) {
        if (this.content && this.content.startsWith(prefix)) return false
      }
      if (this.type === 'user' && !this.displayContent && !this.richHtml && (!this.attachments || this.attachments.length === 0)) return false
      if (this.type === 'assistant' && this.parsedSegments.length === 0 && !this.richHtml) return false
      return true
    },
    quotaInfo() {
      return extractQuotaInfo(this.content)
    },
    isQuotaExhausted() {
      return !!this.quotaInfo
    },
    seatLimitInfo() {
      return extractSeatLimitInfo(this.content)
    },
    isSeatLimit() {
      return !!this.seatLimitInfo
    },
    isStreamingActive() {
      return this.isStreaming && !this.isHistory
    },
    // hasWideComponent() {
    //   // if (this.type !== 'assistant') return false
    //   if (['sop_select', 'sop_1', 'sop_2', 'sop_report', 'sop_report_message'].includes(this.category)) return true
    //   const wideTagNames = ['chart', 'sheet', 'timeline-log', 'result-summary', 'pagelist']
    //   return this.parsedSegments.some(seg => {
    //     if (seg.type === 'component' && wideTagNames.includes(seg.tagName)) return true
    //     if (seg.type === 'component' && seg.tagName === 'card') {
    //       const cardType = (seg.props && (seg.props.type || '')) || ''
    //       if (['loading', 'loading_dynamic', 'sop_select'].includes(cardType)) return true
    //     }
    //     return false
    //   })
    hasWideComponent() {
      // 会话页面或抽屉放大模式下,所有消息均按宽屏布局
      if (this.wideToolCard) return true
      // if (this.type !== 'assistant') return false
      if (['sop_select', 'sop_1', 'sop_2', 'sop_report', 'sop_report_message'].includes(this.category)) return true
      const wideTagNames = ['chart', 'sheet', 'timeline-log', 'result-summary', 'pagelist']
      return this.parsedSegments.some(seg => {
        if (seg.type === 'component' && wideTagNames.includes(seg.tagName)) return true
        if (seg.type === 'component' && seg.tagName === 'card') {
          const cardType = (seg.props && (seg.props.type || '')) || ''
          if (['loading', 'loading_dynamic', 'sop_select'].includes(cardType)) return true
        }
        return false
      })
    },
    sopInfo() {
      return {
        id: this.id,
        messageId: this.id,
        auditId: (this.msgData && this.msgData.auditId) || '',
        liked: 0,
        historyData: this.historyData,
        parent: this.msgParent
      }
    },
    sopType() {
      return this.isHistory ? 'history' : 'active'
    },
    messageClass() {
      return {
        'message-user': this.type === 'user',
        'message-assistant': this.type === 'assistant',
        'message-tool': this.type === 'tool' || this.type === 'toolCall'
      }
    },
    displayContent() {
      if (!this.content) return ''
      let text = this.content
      for (const prefix of SILENT_PREFIXES_LIST) {
        const idx = text.indexOf(prefix)
        if (idx !== -1) {
          text = text.slice(0, idx)
          break
        }
      }
      return text.trim()
    },
    // 用户消息 HTML：转义纯文本，并将 [附件]文件名(路径:xxx) 渲染为文件超链接
    userContentHtml() {
      return renderUserTextWithAttachments(this.displayContent)
    },
    // 用户气泡正文折叠态：超 4 行且未展开时折叠
    isUserTextCollapsed() {
      return this.userTextOverflow && !this.userTextExpanded
    },
    // 追问引用内容（[questioning-action] 之后的部分）
    questioningRef() {
      if (!this.content || this.type !== 'user') return ''
      const idx = this.content.indexOf('[questioning-action]')
      if (idx === -1) return ''
      return this.content.slice(idx + '[questioning-action]'.length).trim()
    },
    // ===== 追问（follow-up）：用户消息正文 + 追问卡片 =====
    followUpSplit() {
      if (this.type !== 'user') return { body: '', items: [] }
      return splitFollowUpsSection(this.displayContent || '')
    },
    followUpItems() {
      return this.followUpSplit.items
    },
    followUpBody() {
      return this.followUpSplit.body
    },
    hasFollowUps() {
      return this.followUpSplit.items.length > 0
    },
    renderFollowUpBodyHtml() {
      return renderUserTextWithAttachments(this.followUpBody || '')
    },
    // ===== 追问（follow-up）：正文高亮注解 =====
    annotationVersion() {
      const list = followUpStore.messageAnnotations(String(this.id))
      return list
        .map((annotation) => {
          const bodyText = (annotation.body || [])
            .map((item) => `${item.type || ''}:${item.text || ''}`)
            .join('/')
          const lastSentText =
            (annotation.meta && annotation.meta.followUp && annotation.meta.followUp.lastSentText) || ''
          return `${annotation.id}:${annotation.updatedAt || ''}:${bodyText}:${lastSentText}`
        })
        .join('|')
    },
    annotationsForMessage() {
      return followUpStore.messageAnnotations(String(this.id))
    },
    // 回复底部操作行：复制 / Markdown
    showTurnActions() {
      if (this.type !== 'assistant') return false
      if (this.isStreamingActive || this.isError) return false
      if (this.isQuotaExhausted || this.isSeatLimit) return false
      return !!this.content
    },
    markdownSource() {
      return this.content || ''
    },
    markdownSourceLineCount() {
      return this.markdownSource.split('\n').length
    },
    imageAttachments() {
      if (!this.attachments) return []
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico']
      return this.attachments.filter(f => {
        if (!f.url && !f.name) return false
        const ext = this._getExtension(f.name || f.url || '')
        return imageExts.includes(ext)
      })
    },
    fileAttachments() {
      if (!this.attachments) return []
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico']
      return this.attachments.filter(f => {
        if (!f.name && !f.url) return false
        const ext = this._getExtension(f.name || f.url || '')
        return !imageExts.includes(ext)
      })
    },
    bubbleStyle() {
      if (this.type === 'user') {
        return {
          backgroundColor: '#ecebf1',
          borderRadius: '8px'
        }
      }
      if (this.type === 'assistant') {
        return {
          backgroundColor: '#f7f8fa',
          borderRadius: '8px'
        }
      }
      return {
        backgroundColor: '#f7f8fa',
        borderRadius: '8px'
      }
    },
    // computed 外壳：任何分段异常都降级为纯文本，避免单块数据把整条消息打空
    _allSegments() {
      try {
        return this._allSegmentsRaw
      } catch (e) {
        console.warn('[CosmoClawMessageItem] 消息分段失败，降级为纯文本渲染', e)
        return this.content
          ? [{ type: 'text', content: String(this.content), _stableKey: 'text-0' }]
          : []
      }
    },
    // 内部分段构建（computed，取值为数组；异常由 _allSegments 外壳捕获）
    _allSegmentsRaw() {
      if (!this.content && (!this.mediaUrls || !this.mediaUrls.length)) return []

      // 在 marked 处理之前，先提取 JSON 代码块替换为占位符
      // 助手消息中的卡片即使解析失败也提取出来，以便展示统一的兜底提示
      const { processedText, extractedBlocks } = this.content
        ? extractJsonCodeBlocks(this.content, this.type === 'assistant')
        : { processedText: '', extractedBlocks: [] }

      const segments = []
      // 按占位符分割文本
      const parts = processedText.split(/(JSON_COMPONENT_\d+_PLACEHOLDER)/g)

      for (const part of parts) {
        if (!part) continue
        const placeholderMatch = part.match(/^JSON_COMPONENT_(\d+)_PLACEHOLDER$/)
        if (placeholderMatch) {
          const idx = parseInt(placeholderMatch[1])
          const block = extractedBlocks[idx]
          if (block) {
            if (block.payload) {
              segments.push({
                type: 'component',
                tagName: block.payload.tagName,
                props: block.payload.props,
                raw: block.raw || '',
                checkKey: this._makeCheckKey(idx, block.raw || '')
              })
            } else if (block.raw) {
              // 卡片形态但本地解析失败（非法 JSON / 结构不符 / 未知组件类型）→ 携带失败原因，交由兜底卡片展示
              segments.push({
                type: 'component',
                tagName: null,
                props: null,
                raw: block.raw,
                errorCode: block.error ? block.error.code : 'json.syntax',
                componentName: block.error ? block.error.componentName : null,
                checkKey: this._makeCheckKey(idx, block.raw),
                needsCheck: true
              })
            }
          }
        } else if (part.trim()) {
          // 流式输出时检测未闭合的代码块或截断的 JSON
          if (this.isStreamingActive) {
            // 1. 通过统计 ``` 出现次数判断最后一段代码块是否已闭合
            //    奇数 → 最后一个 ``` 未闭合，需要特殊处理
            //    偶数 → 所有代码块都已闭合，直接进入 marked 渲染
            const backtickCount = (part.match(/```/g) || []).length
            const isUnclosed = backtickCount % 2 === 1

            if (isUnclosed) {
              const lastIdx = part.lastIndexOf('```')
              const beforeFenced = part.slice(0, lastIdx)
              const afterFenced = part.slice(lastIdx)

              // a) 未闭合的 JSON 组件代码块 → 显示 loading 占位
              const incompleteFenced = afterFenced.match(/```(json|chart|card|task-progress)\s*\n([\s\S]*)$/i)
              if (incompleteFenced) {
                if (beforeFenced.trim()) {
                  segments.push({ type: 'text', content: beforeFenced })
                }
                const partial = incompleteFenced[2] || ''
                // 同时检测 "component" 和 "type" 两种字段名
                const componentMatch = partial.match(/"component"\s*:\s*"([^"]+)"/)
                const typeMatch = !componentMatch ? partial.match(/"type"\s*:\s*"([^"]+)"/) : null
                const matchedType = (componentMatch && componentMatch[1]) || (typeMatch && typeMatch[1]) || ''
                if (matchedType) {
                  const compType = matchedType.toLowerCase()
                  // postMessage 等组件不显示 loading 占位，由 watcher 静默处理
                  if (compType === 'postmessage') continue
                  const typeName = COMPONENT_TYPE_NAMES[compType] || compType
                  const titleMatch = partial.match(/"title"\s*:\s*"([^"]*)"/)
                  const loadingText = titleMatch && titleMatch[1]
                    ? this.$t('workbench.loadingComponent', { name: titleMatch[1], type: typeName })
                    : this.$t('workbench.componentLoading', { type: typeName })
                  segments.push({ type: 'component-loading', loadingText })
                  continue
                }
                // 非 JSON 组件的未闭合代码块 → 跳过，不渲染
                continue
              }

              // b) 普通未闭合代码块 → 只渲染前面的文本
              if (beforeFenced.trim()) {
                segments.push({ type: 'text', content: beforeFenced })
              }
              continue
            }

            // 2. 检测截断的内联 JSON {"component":... 或 {"type":...
            //    只在不在 ``` 代码块内部时触发（避免吞掉整个代码块及后面内容）
            const truncated = part.match(/\{\s*["'](?:component|type)["']\s*:\s*/)
            if (truncated) {
              const beforeJson = part.slice(0, truncated.index)
              // 检查 { 前面是否有未闭合的 ```（即 { 在 ``` 代码块内部）
              const backticksBefore = (beforeJson.match(/```/g) || []).length
              const insideCodeBlock = backticksBefore % 2 === 1
              if (!insideCodeBlock) {
                if (beforeJson.trim()) {
                  segments.push({ type: 'text', content: beforeJson })
                }
                const partial = part.slice(truncated.index)
                const compMatch = partial.match(/"component"\s*:\s*"([^"]+)"/)
                const typeMatch = !compMatch ? partial.match(/"type"\s*:\s*"([^"]+)"/) : null
                const matchedType = (compMatch && compMatch[1]) || (typeMatch && typeMatch[1]) || ''
                if (matchedType) {
                  const compType = matchedType.toLowerCase()
                  if (compType === 'postmessage') continue
                  const typeName = COMPONENT_TYPE_NAMES[compType] || compType
                  const titleMatch = partial.match(/"title"\s*:\s*"([^"]*)"/)
                  const loadingText = titleMatch && titleMatch[1]
                    ? this.$t('workbench.loadingComponent', { name: titleMatch[1], type: typeName })
                    : this.$t('workbench.componentLoading', { type: typeName })
                  segments.push({ type: 'component-loading', loadingText })
                }
                continue
              }
            }
          }
          // 非流式：残留的未闭合卡片 JSON（如被截断）→ 统一兜底提示
          if (!this.isStreamingActive) {
            const brokenIdx = this._findUnparsedCardStart(part)
            if (brokenIdx > -1) {
              const before = part.slice(0, brokenIdx)
              if (before.trim()) segments.push({ type: 'text', content: before })
              // 只截取卡片 JSON 本身（优先括号配对，失败则按空行边界），
              // 其余正文继续按文本/MEDIA 渲染，避免整段尾部被吞进兜底卡片
              const balancedEnd = findBalancedCardEnd(part, brokenIdx)
              const cardEnd = balancedEnd > -1 ? balancedEnd : findTruncatedJsonObjectEnd(part, brokenIdx)
              const cardText = cardEnd > brokenIdx ? part.slice(brokenIdx, cardEnd) : part.slice(brokenIdx).trim()
              const rest = cardEnd > brokenIdx ? part.slice(cardEnd) : ''
              segments.push({
                type: 'component',
                tagName: null,
                props: null,
                raw: cardText.trim(),
                errorCode: 'json.syntax'
              })
              if (rest.trim()) this._pushTextSegments(segments, rest)
              continue
            }
          }
          this._pushTextSegments(segments, part)
        }
      }

      // ── 追加外部 mediaUrls（流式 assistant 事件的 mediaUrls 字段） ──
      // 若文本中已有显式 MEDIA: 行（由 history 或 tool result 注入），
      // 则 mediaUrls 追加到末尾作为补充。
      // 否则，按 \n---\n 拆分文本段落，交错插入 mediaUrls，使文件紧跟对应标题。
      if (this.mediaUrls && this.mediaUrls.length > 0) {
        const hasExplicitMedia = segments.some(s => s.type === 'media-file')
        if (hasExplicitMedia) {
          // 文本中已有 MEDIA: 标记 → 简单追加到末尾
          for (const url of this.mediaUrls) {
            segments.push({ type: 'media-file', source: url })
          }
        } else {
          // 无显式 MEDIA 标记 → 按 --- 分段落，交错插入
          let mi = 0
          const enhanced = []
          for (const seg of segments) {
            if (seg.type === 'text' && mi < this.mediaUrls.length) {
              const parts = seg.content.split(/\n?---\n?/)
              if (parts.length > 1) {
                for (let p = 0; p < parts.length; p++) {
                  const trimmed = parts[p].trim()
                  if (trimmed) {
                    enhanced.push({ type: 'text', content: trimmed })
                  }
                  if (p < this.mediaUrls.length) {
                    enhanced.push({ type: 'media-file', source: this.mediaUrls[p] })
                    mi++
                  }
                }
              } else {
                enhanced.push(seg)
              }
            } else {
              enhanced.push(seg)
            }
          }
          // 剩余未插入的 mediaUrls 追加到末尾
          while (mi < this.mediaUrls.length) {
            enhanced.push({ type: 'media-file', source: this.mediaUrls[mi++] })
          }
          segments.splice(0, segments.length, ...enhanced)
        }
      }

      // 用计数器为每个组件类型生成稳定序号
      const typeCounters = {}
      return segments.map((seg) => {
        let key
        if (seg.type === 'text') {
          key = 'text-' + (typeCounters.text || 0)
          typeCounters.text = (typeCounters.text || 0) + 1
        } else if (seg.type === 'component-loading') {
          key = 'loading-' + (typeCounters.loading || 0)
          typeCounters.loading = (typeCounters.loading || 0) + 1
        } else if (seg.type === 'media-file') {
          key = 'media-' + (typeCounters['media-file'] || 0)
          typeCounters['media-file'] = (typeCounters['media-file'] || 0) + 1
        } else {
          const name = seg.tagName || 'unknown'
          const idx = typeCounters[name] || 0
          key = name + '-' + idx
          typeCounters[name] = idx + 1
        }
        return { ...seg, _stableKey: key }
      })
    },
    // JSON 卡片校验层：启用校验修复器后，对组件 JSON 先校验再渲染
    //  - 未引入 jsonCardChecker 时直接返回 _allSegments，保持原渲染逻辑
    //  - 已引入时：校验中 -> component-checking 占位；校验通过 -> 用修复后 payload 渲染；失败 -> component-invalid 降级
    checkedSegments() {
      try {
        return this._checkedSegmentsRaw
      } catch (e) {
        console.warn('[CosmoClawMessageItem] 卡片校验失败，回退为原始分段', e)
        return this._allSegments
      }
    },
    // 内部卡片校验构建（computed，取值为数组；异常由 checkedSegments 外壳捕获）
    _checkedSegmentsRaw() {
      if (!this._checkerEnabled()) return this._allSegments
      const result = []
      for (const seg of this._allSegments) {
        // 兜底修复成功过：优先用修复后的 payload 渲染，替换原始兜底卡片
        // 按 checkKey（块级唯一）匹配，避免多个相同原文的兜底卡片互相影响
        const fixed = seg.type === 'component'
          ? (seg.checkKey
            ? this.jsonCardFixedMap[seg.checkKey]
            : (seg.raw ? this.jsonCardFixedMap['raw:' + seg.raw] : null))
          : null
        if (fixed && fixed.payload) {
          result.push({
            ...seg,
            tagName: fixed.payload.tagName,
            props: fixed.payload.props,
            _checked: true
          })
          continue
        }
        if (seg.type === 'component' && seg.checkKey) {
          const state = this.jsonCardCheckMap[seg.checkKey]
          if (!state || state.status === 'pending') {
            result.push({ type: 'component-checking', _stableKey: seg._stableKey })
            continue
          }
          if (state.valid && state.payload) {
            // 校验通过/修复成功：用服务端返回的最终 JSON 重新解析出 payload 覆盖渲染
            result.push({
              ...seg,
              tagName: state.payload.tagName,
              props: state.payload.props,
              _checked: true
            })
            continue
          }
          const cardJson = state.cardJson || seg.raw
          const diagnosis = diagnoseComponentJson(cardJson)
          if (!diagnosis && !state.payload) {
            // 本地判定无异常但服务端未给出 payload → 尝试本地解析渲染
            const localPayload = reparseComponentJson(cardJson)
            if (localPayload) {
              result.push({
                ...seg,
                tagName: localPayload.tagName,
                props: localPayload.props,
                _checked: true
              })
              continue
            }
            // 普通 JSON（非组件）→ 还原为代码块纯文本展示
            result.push({
              type: 'text',
              content: '```json\n' + cardJson + '\n```',
              _stableKey: seg._stableKey
            })
            continue
          }
          // 校验失败 / 结构不符合组件定义：统一兜底提示
          result.push({
            type: 'component-invalid',
            raw: cardJson,
            checkKey: seg.checkKey,
            errorCode: diagnosis ? diagnosis.code : 'json.structure',
            componentName: (diagnosis && diagnosis.componentName) || seg.tagName || '',
            _stableKey: seg._stableKey
          })
          continue
        }
        result.push(seg)
      }
      return result
    },
    parsedSegments() {
      const filtered = this.checkedSegments.filter(seg => !(seg.type === 'component' && (seg.tagName === 'task-progress' || seg.tagName === 'iframe' || seg.tagName === 'postmessage')))
      const result = []
      for (const seg of filtered) {
        if (seg.type === 'text') {
          let text = seg.content
          let truncated = false
          for (const prefix of SILENT_PREFIXES_LIST) {
            const idx = text.indexOf(prefix)
            if (idx !== -1) {
              text = text.slice(0, idx)
              truncated = true
              break
            }
          }
          if (!text.trim()) continue
          // ── 将文本段中的 ```mermaid 代码块拆分为独立 mermaid 段 ──
          const parts = text.split(/(```[ \t]*mermaid[ \t]*(?:\r?\n)?[\s\S]*?\r?\n?[ \t]*```)/gi)
          const mermaidRegex = /```[ \t]*mermaid[ \t]*(?:\r?\n)?([\s\S]*?)\r?\n?[ \t]*```/i
          for (const part of parts) {
            if (!part) continue
            const mm = part.match(mermaidRegex)
            if (mm) {
              // 这是一个 mermaid 代码块
              result.push({ type: 'mermaid', code: mm[1].trim(), _stableKey: seg._stableKey + '-mm' })
            } else {
              // 普通文本，与前一个文本段合并
              const last = result.length - 1
              if (last >= 0 && result[last].type === 'text') {
                result[last].content += part
              } else {
                result.push({ type: 'text', content: part, _stableKey: seg._stableKey })
              }
            }
          }
          if (truncated) break
        } else {
          result.push(seg)
        }
      }
      return result
    },
    // 内容闸门：横向步骤条 loading 卡片（stepsLayout="steps"）进度跑满前，隐藏其后的所有段落
    // 跑满后再按单元逐段放出（模拟流式输出）；历史消息直接全量显示
    visibleSegments() {
      const segments = this.parsedSegments
      if (this.isHistory) return segments
      const gateIdx = segments.findIndex(seg =>
        seg.type === 'component' &&
        seg.tagName === 'card' &&
        this._isHoldEnabled(seg.props) &&
        !this.holdDoneKeys[seg._stableKey]
      )
      if (gateIdx !== -1) return segments.slice(0, gateIdx + 1)
      return this._clipByReplay(segments)
    }
  },
  watch: {
    displayContent() {
      this.$nextTick(() => {
        this.userTextExpanded = false
        this.checkUserTextOverflow()
      })
    },
    questioningRef() {
      this.$nextTick(() => {
        this.quoteExpanded = false
        this.checkQuoteOverflow()
      })
    },
    followUpSplit() {
      this.$nextTick(() => {
        // 内容变化（如从会话记录重新加载）时重置为默认收起
        this.userTextExpanded = false
        this.followUpQuoteExpanded = {}
        this.followUpNoteExpanded = {}
        this.checkFollowUpQuoteOverflow()
        this.checkFollowUpNoteOverflow()
      })
    },
    annotationVersion() {
      this.scheduleAnnotationOverlay()
    },
    annotationsForMessage() {
      this.scheduleAnnotationOverlay()
    },
    visibleSegments() {
      this.scheduleAnnotationOverlay()
    },
    _allSegments: {
      immediate: true,
      handler(segments) {
        // 触发 JSON 卡片异步校验，结果写入 jsonCardCheckMap，由 checkedSegments 驱动重新渲染
        this._triggerJsonCardChecks(segments)
      }
    },
    checkedSegments: {
      immediate: true,
      handler(segments) {
        // task-progress: 仅流式输出时处理
        if (!this.isHistory) {
          const tpSeg = segments.find(s => s.type === 'component' && s.tagName === 'task-progress')
          if (tpSeg) {
            const props = tpSeg.props || {}
            const taskList = this._parseTaskProgressItems(props)
            if (taskList.length > 0) {
              this.$store.commit('cosmoClaw/SET_TASK_PROGRESS_LIST', taskList)
              this.$store.commit('cosmoClaw/SET_TASK_PROGRESS_VISIBLE', true)
            }
          }
        }
        // iframe: 历史消息和流式输出都需要处理
        const iframeSeg = segments.find(s => s.type === 'component' && s.tagName === 'iframe')
        if (iframeSeg) {
          const props = iframeSeg.props || {}
          let items = props.items
          if (typeof items === 'string') {
            try { items = JSON.parse(items) } catch { items = [] }
          }
          if (!items && props._rawJson) {
            try { items = JSON.parse(props._rawJson).items } catch { items = [] }
          }
          if (Array.isArray(items) && items.length > 0) {
            // 去重：相同内容不重复提交，避免 iframe 重新加载
            const currentItems = this.$store.state.cosmoClaw.iframeItems
            if (JSON.stringify(items) !== JSON.stringify(currentItems)) {
              this.$store.commit('cosmoClaw/SET_IFRAME_ITEMS', items)
              this.$store.commit('cosmoClaw/SET_IFRAME_VISIBLE', true)
              this.$store.commit('cosmoClaw/SET_IFRAME_ACTIVE_INDEX', 0)
            }
          }
        }
        // postmessage: 仅流式输出时处理
        if (!this.isHistory) {
          const pmSeg = segments.find(s => s.type === 'component' && s.tagName === 'postmessage')
          if (pmSeg) {
            const props = pmSeg.props || {}
            let type = props.type
            let data = props.data
            if (typeof data === 'string') {
              try { data = JSON.parse(data) } catch { /* keep as string */ }
            }
            if (!type && props._rawJson) {
              try {
                const raw = JSON.parse(props._rawJson)
                type = raw.type
                data = raw.data
              } catch { /* ignore */ }
            }
            if (type) {
              // 去重：相同内容不重复提交，避免重复发送 postMessage
              const currentPm = this.$store.state.cosmoClaw.postMessagePayload
              const pmStr = JSON.stringify({ type, data })
              const currentPmStr = currentPm ? JSON.stringify({ type: currentPm.type, data: currentPm.data }) : ''
              if (pmStr !== currentPmStr) {
                this.$store.commit('cosmoClaw/SET_POST_MESSAGE', { type, data })
              }
            }
          }
        }
      }
    }
  },
  methods: {

    // 检测追问引用是否超过 4 行，决定是否显示展开按钮
    checkQuoteOverflow() {
      const el = this.$refs.quoteContent
      if (!el) { this.quoteOverflow = false; return }
      // 先设为 collapsed 态测量可显示的最大高度（4 行，行高 1.5 × 12px = 72px + 少量 padding）
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 18
      const max4Height = lineHeight * 4
      // scrollHeight 是完整展开时的高度
      this.quoteOverflow = el.scrollHeight - max4Height > 2 // 留 2px 容差
    },
    toggleQuote() {
      this.quoteExpanded = !this.quoteExpanded
    },

    // 用户气泡正文：超过 4 行折叠，展开后不再收起
    checkUserTextOverflow() {
      const el = this.$refs.userTextContent
      if (!el) { this.userTextOverflow = false; return }
      // 展开态 clientHeight 等于完整高度，无法判断溢出，沿用上次结果
      if (this.userTextExpanded) return
      // 4 行高度 = 行高 × 4（气泡内行高 26px）
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 26
      this.userTextOverflow = el.scrollHeight - lineHeight * 4 > 2 // 留 2px 容差
    },
    expandUserText() {
      this.userTextExpanded = true
    },

    // ===== 追问卡片：引用文本 4 行折叠 =====
    isFollowUpQuoteExpanded(index) {
      return !!this.followUpQuoteExpanded[index]
    },
    hasFollowUpQuoteOverflow(index) {
      return !!this.followUpQuoteOverflow[index]
    },
    toggleFollowUpQuote(index) {
      this.$set(this.followUpQuoteExpanded, index, !this.followUpQuoteExpanded[index])
      this.$nextTick(() => {
        this.checkFollowUpQuoteOverflow()
      })
    },
    // 折叠态下 scrollHeight 为完整内容高度，clientHeight 为 4 行高度，据此判断是否溢出
    checkFollowUpQuoteOverflow() {
      const root = this.$el
      const nodes = root && typeof root.querySelectorAll === 'function'
        ? root.querySelectorAll('[data-followup-quote]')
        : []
      const next = {}
      nodes.forEach((el) => {
        const index = el.getAttribute('data-followup-quote')
        if (index === null) return
        // 展开态 clientHeight 等于完整高度，无法判断是否溢出，沿用上次结果
        next[index] = this.followUpQuoteExpanded[index]
          ? !!this.followUpQuoteOverflow[index]
          : el.scrollHeight - el.clientHeight > 2
      })
      this.followUpQuoteOverflow = next
    },

    // ===== 追问卡片：引用后正文（note）4 行折叠 =====
    isFollowUpNoteExpanded(index) {
      return !!this.followUpNoteExpanded[index]
    },
    hasFollowUpNoteOverflow(index) {
      return !!this.followUpNoteOverflow[index]
    },
    expandFollowUpNote(index) {
      this.$set(this.followUpNoteExpanded, index, true)
    },
    checkFollowUpNoteOverflow() {
      const root = this.$el
      const nodes = root && typeof root.querySelectorAll === 'function'
        ? root.querySelectorAll('[data-followup-note]')
        : []
      const next = {}
      nodes.forEach((el) => {
        const index = el.getAttribute('data-followup-note')
        if (index === null) return
        next[index] = this.followUpNoteExpanded[index]
          ? !!this.followUpNoteOverflow[index]
          : el.scrollHeight - el.clientHeight > 2
      })
      this.followUpNoteOverflow = next
    },

    formatPoints(value) {
      if (value === null || value === undefined || value === '') return '0'
      const num = Number(value)
      if (Number.isNaN(num)) return String(value)
      // 最多保留两位小数，去掉多余的 0
      return parseFloat(num.toFixed(2)).toString()
    },
    handleQuotaAction() {
      // 点击「申请配额」弹出与数字人介绍页一致的申请试用弹窗
      this.trialApplyVisible = true
      this.$emit('quota-recharge', this.quotaInfo)
    },
    // ===== JSON 卡片校验/修复相关 =====
    _checkerEnabled() {
      return typeof window !== 'undefined' && typeof window.__JSON_CARD_CHECKER__ === 'function'
    },
    // 查找文本中未被提取的卡片 JSON 起点（位于行首且不在代码块内）
    _findUnparsedCardStart(text) {
      const regex = /\{\s*["'](component|type)["']\s*:\s*(?:["']([a-zA-Z0-9_-]*))?/g
      let match
      while ((match = regex.exec(text)) !== null) {
        // type 字段的取值必须是受支持的组件名，避免普通 JSON 示例被当成卡片
        if (match[1].toLowerCase() === 'type' && !COMPONENT_TYPE_NAMES[(match[2] || '').toLowerCase()]) continue
        const before = text.slice(0, match.index)
        const atLineStart = match.index === 0 || /\n\s*$/.test(before)
        if (!atLineStart) continue
        // 位于未闭合/已闭合的 ``` 代码块内部时跳过，交由代码块逻辑处理
        if ((before.match(/```/g) || []).length % 2 === 1) continue
        return match.index
      }
      return -1
    },
    // 按 MEDIA:<path> 行交错拆分为「文本 / 文件」分段，保持原文顺序
    // 例如 "标题A\nMEDIA:fileA\n标题B\nMEDIA:fileB"
    // → [text:标题A] [media:fileA] [text:标题B] [media:fileB]
    _pushTextSegments(segments, textContent) {
      const mediaRegex = /^MEDIA:(.+?)(?:\n|$)/gm
      if (!mediaRegex.test(textContent)) {
        segments.push({ type: 'text', content: textContent })
        return
      }
      mediaRegex.lastIndex = 0
      let lastIdx = 0
      let mm
      while ((mm = mediaRegex.exec(textContent)) !== null) {
        const before = textContent.slice(lastIdx, mm.index).trim()
        if (before) {
          segments.push({ type: 'text', content: before })
        }
        segments.push({ type: 'media-file', source: mm[1] })
        lastIdx = mm.index + mm[0].length
      }
      const after = textContent.slice(lastIdx).trim()
      if (after) {
        segments.push({ type: 'text', content: after })
      }
    },
    _hashString(str) {
      let hash = 0
      if (!str) return 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
        hash |= 0
      }
      return Math.abs(hash)
    },
    _makeCheckKey(blockIdx, raw) {
      const prefix = this.id !== undefined && this.id !== null ? String(this.id) : 'msg'
      return `${prefix}-${blockIdx}-${raw ? raw.length : 0}-${this._hashString(raw || '')}`
    },
    _triggerJsonCardChecks(segments) {
      if (!this._checkerEnabled()) return
      for (const seg of segments || []) {
        if (seg.type === 'component' && seg.checkKey && seg.raw && !this.jsonCardCheckMap[seg.checkKey]) {
          this._runJsonCardCheck(seg)
        }
      }
    },
    async _runJsonCardCheck(seg) {
      const key = seg.checkKey
      if (!key) return
      this.$set(this.jsonCardCheckMap, key, { status: 'pending' })
      let result = null
      try {
        result = await window.__JSON_CARD_CHECKER__(seg.raw)
      } catch (e) {
        result = null
      }
      let payload = null
      if (result && result.valid && result.cardJson) {
        // 校验通过：用修复后的 JSON 重新解析出组件 payload
        payload = reparseComponentJson(result.cardJson)
      }
      this.$set(this.jsonCardCheckMap, key, {
        status: 'done',
        valid: !!(result && result.valid),
        cardJson: (result && result.cardJson) || seg.raw,
        payload,
        errorMessage: (result && result.errorMessage) || null,
        repairAttempts: (result && result.repairAttempts) || 0
      })
    },
    // 兜底卡片「修复」成功：用修复后的 JSON 覆盖校验结果，驱动重新渲染以替换原始内容
    handleComponentFixed(seg, result) {
      if (!seg || !result || !result.valid || result.fallback || !result.cardJson) return
      const payload = result.payload || reparseComponentJson(result.cardJson)
      if (!payload) return
      // 按分段唯一标识存放：checkKey 含块序号，可区分“原文相同的多个兜底卡片”；
      // 仅在无 checkKey 的兜底路径才退回 raw key
      const fixedKey = seg.checkKey || (seg.raw ? 'raw:' + seg.raw : null)
      if (!fixedKey) return
      this.$set(this.jsonCardFixedMap, fixedKey, {
        cardJson: result.cardJson,
        payload
      })
      if (!seg.checkKey) return
      this.$set(this.jsonCardCheckMap, seg.checkKey, {
        status: 'done',
        valid: true,
        cardJson: result.cardJson,
        payload,
        errorMessage: result.errorMessage || null,
        repairAttempts: result.repairAttempts || 0
      })
    },
    // ===== END JSON 卡片校验/修复 =====

    _getExtension(name) {
      if (!name) return ''
      const parts = name.split('.')
      return parts.length > 1 ? parts.pop().toLowerCase() : ''
    },
    getFileTypeColor(name) {
      const ext = this._getExtension(name)
      const colorMap = {
        pdf: '#E53935', doc: '#1E88E5', docx: '#1E88E5',
        xls: '#43A047', xlsx: '#43A047', csv: '#26A69A',
        ppt: '#FB8C00', pptx: '#FB8C00',
        js: '#7E57C2', ts: '#7E57C2', py: '#7E57C2', java: '#7E57C2',
        txt: '#78909C', md: '#78909C',
        zip: '#8D6E63', rar: '#8D6E63', '7z': '#8D6E63',
        mp4: '#AB47BC', avi: '#AB47BC', mov: '#AB47BC',
        mp3: '#5C6BC0', wav: '#5C6BC0'
      }
      return colorMap[ext] || '#90A4AE'
    },
    getFileTypeLabel(name) {
      const ext = this._getExtension(name)
      const labelMap = {
        pdf: 'PDF', doc: 'DOC', docx: 'DOC',
        xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
        ppt: 'PPT', pptx: 'PPT',
        js: 'CODE', ts: 'CODE', py: 'CODE', java: 'CODE',
        txt: 'TXT', md: 'TXT',
        zip: 'ZIP', rar: 'RAR', '7z': '7Z',
        mp4: 'MP4', avi: 'AVI', mov: 'MOV',
        mp3: 'MP3', wav: 'WAV'
      }
      return labelMap[ext] || ext.toUpperCase().slice(0, 4) || 'FILE'
    },
    previewImage(url) {
      this.openImagePreview(url, '')
    },
    openImagePreview(url, name) {
      this.imagePreviewUrl = url
      this.imagePreviewName = name || url.split('/').pop() || ''
      this.imagePreviewVisible = true
    },
    handleEscKey(e) {
      if (e.key === 'Escape' && this.imagePreviewVisible) {
        this.imagePreviewVisible = false
      }
    },
    downloadPreviewImage() {
      if (!this.imagePreviewUrl) return
      const a = document.createElement('a')
      a.href = this.imagePreviewUrl
      a.download = this.imagePreviewName || 'image'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    downloadAttachment(file) {
      if (!file.url) return
      const a = document.createElement('a')
      a.href = file.url
      a.download = file.name || 'download'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    renderMarkdown(text) {
      if (!text) return ''
      try {
        // 【分析过程】是协议标记，只用于判定这段是过程还是结论，绝不能到用户眼前。
        // 过程行在 trace 里已经剥过；这里兜住"模型把标记打在正式结论上"的违约情况。
        // 无标记时 stripProcessMarker 逐字节原样返回，既有渲染不受影响。
        let cleanText = stripProcessMarker(text)

        // 初始化测试标记（仅运行一次）
        if (!window.__markedTested) {
          window.__markedTested = true
          const t = '## test\n\n**bold**'
          const r = marked.parse(t)
          if (!r.includes('<h2') || !r.includes('<strong')) {
            console.warn('[renderMarkdown] marked library may not work correctly')
          }
        }

        // ── 处理未闭合的代码块 ──
        const btCount = (cleanText.match(/```/g) || []).length
        if (btCount % 2 === 1) {
          const lastIdx = cleanText.lastIndexOf('```')
          const lineStart = cleanText.lastIndexOf('\n', lastIdx)
          const removeStart = lineStart === -1 ? 0 : lineStart
          cleanText = cleanText.slice(0, removeStart) + cleanText.slice(lastIdx + 3)
        }

        let result = marked.parse(cleanText)

        // ── 兜底：清除结果中残留的未解析 **text** 标记 ──
        // marked 可能因流式拼合等边缘情况遗漏部分加粗，逐一清除剩余 **
        result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

        // ── [附件]文件名 (路径:xxx) → 文件图标 + 超链接 ──
        // 匹配: [附件]report.pdf (路径:/app/data/chat-uploads/xxx/report.pdf)
        result = result.replace(
          /\[附件\]([^\s(]+)\s*\(路径:([^)]+)\)/g,
          (_, fileName, filePath) =>
            `<a class="chat-attachment" href="${filePath}" target="_blank"><i class="el-icon-document"></i>${fileName}</a>`
        )

        // ── 可选：表格铺满（只在源文本带 <!--md-table:full--> 标记时生效）──
        // 无标记时不进入替换，其它回答的表格渲染与原先完全一致
        if (cleanText.includes('md-table:full')) {
          result = result.replace(
            /<!--\s*md-table:full\s*-->\s*(<table[\s\S]*?<\/table>)/g,
            '<div class="md-table-full">$1</div>'
          )
        }

        return result
      } catch (e) {
        console.debug('[renderMarkdown]', e.message)
        return text
      }
    },
    _parseTaskProgressItems(props) {
      const parse = (val) => {
        if (!val) return null
        if (Array.isArray(val)) return val
        if (typeof val === 'object') return val
        if (typeof val !== 'string') return null
        try { return JSON.parse(val.replace(/\n/g, '')) } catch {
          try { return JSON.parse(val.replace(/\n/g, '').replace(/'/g, '"').replace(/,\s*([}\]])/g, '$1')) } catch { return null }
        }
      }
      const raw = parse(props._rawJson) || {}
      let items = parse(props.items) || parse(raw.items) || []
      if (!Array.isArray(items)) items = []
      return items.map(item => ({
        name: item.name || item.label || '',
        finished: !!item.finished || !!item.checked
      }))
    },
    // 卡片是否开启「进度跑满后再显示后续内容」：
    // 横向 5 步进度条形态的 loading 卡片（stepsLayout="steps"）即视为开启
    _isHoldEnabled(props) {
      if (!props) return false
      return props.type === 'loading' && props.stepsLayout === 'steps'
    },
    // loading 卡片进度跑满 → 放行后续段落
    // instant=true（历史/已完成）时不补渐显，直接一次性展示
    handleLoadingDone(stableKey, payload) {
      if (!stableKey) return
      this.$set(this.holdDoneKeys, stableKey, true)
      if (this.isHistory || (payload && payload.instant)) return
      // 仅步骤条形态的 loading 卡片才接管后续内容的渐显，其它 loading 卡片保持原样
      const seg = this.parsedSegments.find(s => s._stableKey === stableKey)
      if (!seg || !this._isHoldEnabled(seg.props)) return
      this._startReplay(stableKey)
    },
    // 从闸门卡片之后开始逐单元放出内容
    _startReplay(gateKey) {
      this._stopReplay()
      if (!gateKey) return
      this.replay.active = true
      this.replay.gateKey = gateKey
      this.replay.segIndex = 0
      this.replay.unitIndex = 0
      this.replayTimer = setInterval(() => this._tickReplay(), 100)
    },
    _stopReplay() {
      if (this.replayTimer) {
        clearInterval(this.replayTimer)
        this.replayTimer = null
      }
      this.replay.active = false
      this.replay.gateKey = ''
      this.replay.segIndex = 0
      this.replay.unitIndex = 0
    },
    // 每次只多放出一个单元；当前段落放完后前进到下一段
    _tickReplay() {
      const r = this.replay
      if (!r.active) return
      const segments = this.parsedSegments
      const gateIdx = segments.findIndex(seg => seg._stableKey === r.gateKey)
      if (gateIdx === -1) return this._stopReplay()
      const after = segments.slice(gateIdx + 1)
      const cur = after[r.segIndex]
      if (!cur) {
        // 已放到末尾：上游仍在流式输出则等待新段落，否则结束
        if (!this.isStreaming) this._stopReplay()
        return
      }
      if (cur.type === 'text') {
        const total = splitRevealUnits(cur.content).length
        if (r.unitIndex < total) {
          r.unitIndex += 1
          this._followScroll()
          return
        }
      }
      r.segIndex += 1
      r.unitIndex = 0
      this._followScroll()
    },
    // 回放期间保持聊天区贴底（复用列表组件的滚动事件，尊重用户手动接管）
    _followScroll() {
      this.$nextTick(() => {
        document.dispatchEvent(new CustomEvent('cosmo-claw-scroll-bottom'))
      })
    },
    // 按回放进度裁剪闸门之后的段落
    _clipByReplay(segments) {
      const r = this.replay
      if (!r.active) return segments
      const gateIdx = segments.findIndex(seg => seg._stableKey === r.gateKey)
      if (gateIdx === -1) return segments
      const head = segments.slice(0, gateIdx + 1)
      const after = segments.slice(gateIdx + 1)
      const revealed = after.slice(0, r.segIndex)
      const cur = after[r.segIndex]
      if (cur && cur.type === 'text' && r.unitIndex > 0) {
        const units = splitRevealUnits(cur.content).slice(0, r.unitIndex)
        revealed.push(Object.assign({}, cur, { _revealUnits: units }))
      }
      return head.concat(revealed)
    },
    // 文本段渲染：渐显中的段落只渲染已放出的那部分
    renderSegmentText(seg) {
      const text = seg._revealUnits ? seg._revealUnits.join('') : seg.content
      return this.renderMarkdown(text)
    },
    parseProps(props, stableKey) {
      const result = {}
      for (const key of Object.keys(props)) {
        const val = props[key]
        if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
          result[key] = val
        } else {
          result[key] = val
        }
      }
      if (!result._componentKey) {
        const baseKey = generateComponentKey(props.type || 'card', props)
        const msgPrefix = this.id ? this.id.slice(-8) : ''
        result._componentKey = msgPrefix + '-' + (stableKey ? baseKey + '-' + stableKey : baseKey)
      }
      return result
    },
    parsePropsChatCard(props, stableKey) {
      const base = this.parseProps(props, stableKey)
      const tryParse = (val) => {
        if (typeof val !== 'string') return val
        if (!(val.startsWith('{') || val.startsWith('['))) return val
        try { return JSON.parse(val) } catch (e) { return val }
      }
      if (base.initialState) base.initialState = tryParse(base.initialState)
      if (base.fields) base.fields = tryParse(base.fields)
      return base
    },
    // WS card → SopSelect info
    toSopCardInfo(seg) {
      return {
        id: this.id,
        messageId: this.id,
        auditId: (seg.props && seg.props.auditId) || '',
        liked: 0,
        historyData: null
      }
    },
    handleCardAction(detail) {
      if (detail && detail.message) {
        this.$store.dispatch('cosmoClaw/sendMessage', {
          content: detail.message
        })
      }
    },
    // ===== 追问：选中文本 → 悬浮 island =====
    handleSelectionPointerDown(e) {
      this._selectionPointer = { x: e.clientX, y: e.clientY, at: Date.now() }
    },
    // 选区可能在气泡外松手（拖选时向上滚动、越过内边距/滚动条），
    // 因此监听挂到 document 上，再按选区归属判断是否属于本条消息。
    handleDocumentMouseUp(e) {
      if (e && typeof e.button === 'number' && e.button !== 0) return
      if (!this.$refs.markdownRoot) return
      if (e && e.target && e.target.closest && e.target.closest('.followup-island')) return
      this.handleTextSelection()
    },
    handleTextSelection() {
      const root = this.$refs.markdownRoot
      if (!root) return
      if (this.isStreamingActive) return
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return
      const range = selection.getRangeAt(0)
      if (!root.contains(range.commonAncestorContainer)) return
      const quote = selection.toString()
      if (!quote || !quote.trim()) return
      const segments = collectTextSegments(root)
      const start = resolveNodeOffset(segments, range.startContainer, range.startOffset)
      const end = resolveNodeOffset(segments, range.endContainer, range.endOffset)
      if (end <= start) return
      const rect = range.getBoundingClientRect()
      if (!rect || (rect.width === 0 && rect.height === 0)) return
      const pointer = this._selectionPointer
      const pointerFresh = pointer && Date.now() - pointer.at <= SELECTION_POINTER_MAX_AGE_MS
      const anchorX = clamp(
        pointerFresh ? pointer.x : rect.left + rect.width / 2,
        rect.left,
        rect.right
      )
      const anchorY = Math.max(36, rect.top - 8)
      this._selectionPointer = null
      followUpStore.openCompactIsland({
        messageId: String(this.id),
        anchorX,
        anchorY,
        selection: { start, end, quote: quote.trim() },
        range: range.cloneRange()
      })
    },
    handleAnnotationChipClick(chip) {
      if (!chip || !chip.annotation || chip.state === 'sent') return
      const root = this.$refs.markdownRoot
      if (!root) return
      const rootRect = root.getBoundingClientRect()
      followUpStore.openEditIsland({
        messageId: String(this.id),
        annotationId: chip.annotation.id,
        anchorX: rootRect.left + chip.left,
        anchorY: Math.max(36, rootRect.top + chip.top - 8)
      })
    },
    scheduleAnnotationOverlay() {
      if (this._overlayFrame) return
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        this.refreshAnnotationOverlay()
        return
      }
      this._overlayFrame = window.requestAnimationFrame(() => {
        this._overlayFrame = null
        this.refreshAnnotationOverlay()
      })
    },
    refreshAnnotationOverlay() {
      const root = this.$refs.markdownRoot
      const annotations = this.annotationsForMessage
      if (!root || !annotations || annotations.length === 0) {
        this._overlaySig = ''
        if (this.annotationRects.length) this.annotationRects = []
        if (this.annotationChips.length) this.annotationChips = []
        return
      }
      const segments = collectTextSegments(root)
      const fullText = segments.map((segment) => segment.node.nodeValue || '').join('')
      const resolved = resolveTextAnnotations(fullText, annotations)
      const geometry = computeAnnotationOverlayGeometry({
        root,
        resolvedAnnotations: resolved,
        annotationIndexOverrides: followUpStore.getAnnotationIndexMap(String(this.id))
      })
      const signature = JSON.stringify([
        geometry.rects.map((rect) => [
          rect.key,
          Math.round(rect.left),
          Math.round(rect.top),
          Math.round(rect.width),
          Math.round(rect.height),
          rect.state
        ]),
        geometry.chips.map((chip) => [
          chip.key,
          Math.round(chip.left),
          Math.round(chip.top),
          chip.index,
          chip.state
        ])
      ])
      if (signature === this._overlaySig) return
      this._overlaySig = signature
      this.annotationRects = geometry.rects
      this.annotationChips = geometry.chips
    },
    annotationRectStyle(rect) {
      const visual = getAnnotationRectVisual(rect.state)
      return {
        left: `${rect.left - 4}px`,
        top: `${rect.top - 1}px`,
        width: `${rect.width + 8}px`,
        height: `${rect.height + 2}px`,
        borderRadius: '4px',
        backgroundColor: visual.background,
        opacity: visual.opacity
      }
    },
    annotationChipStyle(chip) {
      const visual = getAnnotationChipVisual(chip.state)
      return {
        left: `${chip.left}px`,
        top: `${chip.top}px`,
        backgroundColor: visual.background,
        color: visual.color,
        cursor: visual.cursor
      }
    },
    // ===== 回复底部：复制 / Markdown 原文 =====
    async _writeClipboard(text) {
      const value = typeof text === 'string' ? text : ''
      if (!value) return false
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(value)
          return true
        }
      } catch (e) {
        // 忽略，走兜底方案
      }
      try {
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.setAttribute('readonly', 'readonly')
        textarea.style.position = 'fixed'
        textarea.style.top = '-1000px'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(textarea)
        return ok
      } catch (e) {
        return false
      }
    },
    async handleCopyMessage() {
      const text = this.displayContent || this.content || ''
      if (!text) return
      const ok = await this._writeClipboard(text)
      if (!ok) return
      this.copied = true
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copied = false
      }, 2000)
    },
    openMarkdownDialog() {
      this.markdownCopied = false
      this.markdownDialogVisible = true
      this._lockBodyScroll()
      document.addEventListener('keydown', this.handleMarkdownOverlayKeydown, true)
    },
    closeMarkdownDialog() {
      this.markdownDialogVisible = false
      this._unlockBodyScroll()
      document.removeEventListener('keydown', this.handleMarkdownOverlayKeydown, true)
    },
    handleMarkdownOverlayKeydown(event) {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      event.preventDefault()
      this.closeMarkdownDialog()
    },
    _lockBodyScroll() {
      this._prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    _unlockBodyScroll() {
      if (this._prevBodyOverflow === undefined) return
      document.body.style.overflow = this._prevBodyOverflow
      this._prevBodyOverflow = undefined
    },
    async handleCopyMarkdownSource() {
      const ok = await this._writeClipboard(this.markdownSource)
      if (!ok) return
      this.markdownCopied = true
      if (this.markdownCopiedTimer) clearTimeout(this.markdownCopiedTimer)
      this.markdownCopiedTimer = setTimeout(() => {
        this.markdownCopied = false
      }, 2000)
    },
    handleDrawerAction(detail) {
      if (detail && detail.message) {
        this.$store.dispatch('cosmoClaw/sendMessage', {
          content: detail.message
        })
      }
    },
    getCenterIframeName(props) {
      if (!props) return ''
      let params = props.params
      if (typeof params === 'string') { try { params = JSON.parse(params) } catch { params = null } }
      if (!params && props._rawJson) { try { params = JSON.parse(props._rawJson).params } catch { params = null } }
      return (params && params.name) || ''
    },
    handleCenterIframeClick(props) {
      if (!props) return
      let params = props.params
      if (typeof params === 'string') { try { params = JSON.parse(params) } catch { params = null } }
      if (!params && props._rawJson) { try { params = JSON.parse(props._rawJson).params } catch { params = null } }
      if (params && params.url) {
        this.$store.commit('cosmoClaw/SET_CENTER_IFRAME', { url: params.url, name: params.name || '' })
      }
    },
    handleMarkdownClick(e) {
      const target = e.target && e.target.closest && e.target.closest('[data-smart-action]')
      const legacyAction = target ? null : this.getLegacySmartAction(e.target)
      if (!target && !legacyAction) return
      e.preventDefault()
      e.stopPropagation()
      document.dispatchEvent(new CustomEvent('cosmo-claw-smart-action', {
        detail: legacyAction || {
          action: target.getAttribute('data-smart-action'),
          plan: target.getAttribute('data-plan'),
          indicator: target.getAttribute('data-indicator')
        }
      }))
    },

    getLegacySmartAction(rawTarget) {
      const clickable = rawTarget && rawTarget.closest && rawTarget.closest('button,a')
      if (!clickable || !['应用', '创建'].includes(clickable.textContent.trim())) return null
      let node = clickable.parentElement
      const root = this.$el.querySelector('.markdown-body')
      while (node && node !== root) {
        const text = node.textContent || ''
        if (clickable.textContent.trim() === '创建' && text.includes('新指标') && text.includes('负责人')) {
          const spans = Array.from(node.querySelectorAll('span'))
          const indicatorSpan = spans.find((span) => {
            const value = span.textContent.trim()
            return value && value !== '新指标'
          })
          return {
            action: 'create-single-indicator',
            indicator: (indicatorSpan && indicatorSpan.textContent.trim()) || '产品结构优化率'
          }
        }
        if (text.includes('当前') && text.includes('目标') && text.includes('负责人')) {
          const spans = Array.from(node.querySelectorAll('span'))
          const indicatorSpan = spans.find((span) => {
            const value = span.textContent.trim()
            return value && !['核心指标', '关键指标', '辅助指标'].includes(value) && !value.includes('当前') && !value.includes('目标')
          })
          return {
            action: 'apply-single-indicator',
            indicator: (indicatorSpan && indicatorSpan.textContent.trim()) || '毛利率'
          }
        }
        node = node.parentElement
      }
      return null
    },
    formatTime(ts) {
      if (!ts) return ''
      const numTs = Number(ts)
      if (!Number.isFinite(numTs)) return ''
      const d = new Date(numTs)
      const pad = (n) => String(n).padStart(2, '0')
      const hhmm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
      // 当天消息只展示时间；非当天消息展示 日期 + 时间
      const now = new Date()
      const sameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      if (sameDay) return hhmm
      const datePart =
        d.getFullYear() === now.getFullYear()
          ? `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
          : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      return `${datePart} ${hhmm}`
    },
    _simpleHash(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
        hash = hash & hash
      }
      return Math.abs(hash).toString(36)
    }
  }
}
</script>

<style lang="scss" scoped>
.message-item {
  margin-bottom: 16px;
  display: flex;

  &.message-user {
    justify-content: flex-end;
  }

  &.message-assistant,
  &.message-tool {
    justify-content: flex-start;
  }
  // &.message-assistant {
  //   width: 70%;
  // }
}

.message-wrapper {
  // max-width: 85%;
  max-width: 100%;
  display: flex;
  flex-direction: column;

  &.is-user {
    align-items: flex-end;
    // 参考 COSMO-Sphere Client: 用户气泡占满消息流宽度,内容靠右
    max-width: 100%;
  }

  &.has-wide-component {
    // 参考 COSMO-Sphere Client: 思考卡片/宽屏组件占满消息流宽度
    width: 100%;
  }
}

.message-bubble {
  padding: 10px 16px;
  word-break: break-word;
  line-height: 1.6;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid #e8ecff;
  background: rgba(255, 255, 255, 0.8);
}

// 用户气泡：浅蓝背景 + 深色文字 + 16px 字号
.message-wrapper.is-user .message-bubble {
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  max-width: 80%;
  background: rgba(74, 95, 243, 0.1);
  color: #333;
  font-size: 16px;
  line-height: 26px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  // 让气泡内的段落、链接、行内代码参考目标项目样式
  ::v-deep p { margin: 0; }
  ::v-deep a { text-decoration: underline; }
  ::v-deep code {
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.92em;
  }
}

.message-text {
  white-space: pre-wrap;
  color: #1f2329;
}

/* 用户气泡正文：超 4 行折叠（区别于追问引用：底部渐隐 + 居中「展开全部」） */
.message-text--collapsible {
  display: flex;
  flex-direction: column;
}

.user-text-content {
  line-height: 26px; // 与折叠高度 104px（4 行）保持一致

  &.is-collapsed {
    max-height: 104px; // 4 行 × 26px 行高
    overflow: hidden;
    -webkit-mask-image: linear-gradient(180deg, #000 calc(100% - 22px), rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(180deg, #000 calc(100% - 22px), rgba(0, 0, 0, 0) 100%);
  }
}

.user-text-toggle {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #606266;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;

  &:hover {
    color: #4a5ff3;
  }
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.attachment-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-image-item {
  max-width: 240px;
  max-height: 240px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.2s;
  }

  &:hover img {
    transform: scale(1.02);
  }
}

.attachment-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  max-width: 260px;
  transition: all 0.2s;

  &:hover {
    border-color: #4A5FF3;
    box-shadow: 0 2px 6px rgba(74, 95, 243, 0.15);
  }

  .attachment-type-badge {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    span {
      font-size: 10px;
      font-weight: 700;
      color: #fff;
    }
  }

  .attachment-file-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: #1f2329;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(51, 112, 255, 0.08);
  border-radius: 6px;
  font-size: 12px;
  color: #3370ff;

  i { font-size: 13px; }
}

.message-time {
  font-size: 12px;
  color: #aaaaaa;
  margin-top: 4px;
  padding: 0 4px;
}

/* ========== 追问引用样式 ========== */
.questioning-quote {
  margin-top: 8px;
  padding: 8px 12px;
  background: #e7edfe;
  border-radius: 6px;
  position: relative;
}
.quote-label {
  font-size: 11px;
  color: #999999;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.quote-content {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  word-break: break-all;
  white-space: pre-wrap;
  transition: max-height 0.25s ease;
}
.quote-content.is-collapsed {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}
.quote-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
  font-size: 11px;
  color: #4a5ff3;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #3b4ce5;
  }

  i {
    font-size: 10px;
  }
}

.quota-exhausted-card {
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 8px;
  padding: 20px 24px;
  width: 360px;
  min-width: 360px;
  box-sizing: border-box;
}

.quota-exhausted-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quota-exhausted-title {
  font-weight: 600;
  font-size: 18px;
  color: #F56C6C;
  line-height: 24px;
}

.quota-exhausted-desc {
  margin-top: 12px;
  font-size: 14px;
  color: #F56C6C;
  line-height: 20px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.quota-exhausted-stats {
  display: flex;
  align-items: flex-start;
  gap: 40px;
  margin-top: 16px;
}

.quota-stat {
  display: flex;
  flex-direction: column;
}

.quota-stat-label {
  font-size: 12px;
  color: #F56C6C;
  line-height: 16px;
}

.quota-stat-value {
  margin-top: 4px;
  font-weight: 600;
  font-size: 20px;
  color: #F56C6C;
  line-height: 24px;
}

.quota-exhausted-footer {
  margin-top: 18px;
  border-top: 1px solid #fbc4c4;
  padding-top: 12px;
}

.quota-exhausted-btn {
  display: inline-block;
  padding: 10px 28px;
  background: #fde2e2;
  border: 1px solid #F56C6C;
  border-radius: 6px;
  font-size: 14px;
  color: #F56C6C;
  cursor: pointer;
  transition: background 0.2s;
}

.quota-exhausted-btn:hover {
  background: #f8d3d3;
}

/* ===== 席位上限提示卡片 ===== */
.seat-limit-card {
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 8px;
  padding: 18px 20px;
  width: 380px;
  min-width: 380px;
  box-sizing: border-box;
}

.seat-limit-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seat-limit-icon {
  font-size: 18px;
  color: #F56C6C;
}

.seat-limit-title {
  font-weight: 600;
  font-size: 16px;
  color: #F56C6C;
  line-height: 22px;
}

.seat-limit-desc {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
  line-height: 20px;
}

.seat-limit-stats {
  display: flex;
  align-items: flex-start;
  gap: 48px;
  margin-top: 16px;
}

.seat-stat {
  display: flex;
  flex-direction: column;
}

.seat-stat-label {
  font-size: 12px;
  color: #999;
  line-height: 16px;
}

.seat-stat-value {
  margin-top: 4px;
  font-weight: 600;
  font-size: 20px;
  color: #F56C6C;
  line-height: 24px;
}

.seat-limit-footer {
  margin-top: 16px;
  font-size: 12px;
  color: #999;
  line-height: 18px;
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #f5222d;
  padding: 8px 12px;
  background: #fff2f0;
  border-radius: 6px;
  font-size: 13px;
}

/* ========== 回复底部操作行（复制 / Markdown） ========== */
.turn-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 9px 13px 15px;
  margin: 12px -20px -15px;
  border-top: 1px solid rgba(26, 27, 31, 0.015);
  background: rgba(26, 27, 31, 0.015);
  border-radius: 0 0 7px 7px;
  font-size: 13px;
  line-height: 1.5;
}

.turn-action-group {
  display: flex;
  align-items: center;
  gap: 11px;
}

.turn-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: #848689;
  font-size: 13px;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;

  i {
    font-size: 12px;
    line-height: 1;
  }

  &:hover {
    color: #1a1b1f;
  }

  &:focus {
    outline: none;
  }

  &.is-success {
    color: #16a34a;
  }
}

/* ========== 追问：正文高亮 overlay ========== */
.markdown-body-wrap {
  position: relative;
}

.annotation-overlay {
  --info: #4a5ff3;
  --background: #ffffff;
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.annotation-overlay--chips {
  z-index: 2;
}

.annotation-rect {
  position: absolute;
  pointer-events: none;
  transition: opacity 0.15s;
}

.annotation-chip {
  position: absolute;
  box-sizing: border-box;
  min-width: 16px;
  height: 15px;
  padding: 0 3px;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  text-align: center;
  user-select: none;
  pointer-events: auto;
  transform: translate(-2px, -8px);
  transition: background 0.15s;
  outline: none;
}

/* ========== 用户消息中的追问卡片 ========== */
.message-wrapper.is-user .message-bubble.has-followups {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.user-followup-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
}

.followup-body-bubble {
  max-width: 100%;
  padding: 10px 16px;
  border-radius: 8px;
  background: rgba(74, 95, 243, 0.1);
  color: #333;
  font-size: 16px;
  line-height: 1.6;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  ::v-deep p { margin: 0; }
  ::v-deep a { text-decoration: underline; }
  ::v-deep code {
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.92em;
  }
}

.followup-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.followup-block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
  color: #4a5ff3;
}

.followup-block-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  object-fit: contain;
}

.followup-card {
  width: 100%;
  overflow: hidden;
  border-radius: 8px 0 8px 8px;
  user-select: text;
}

.followup-card-quote {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 19px;
  border: 1px solid #ddd;
  border-bottom: 0;
  background: rgba(74, 95, 243, 0.05);
}

.followup-card-quote-text {
  flex: 1;
  min-width: 0;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-size: 12px;
  line-height: 22px;
  color: #999;
  /* 用 max-height 限高（line-clamp 对 pre-wrap 换行文本不生效），4 行 × 22px */
  max-height: 88px;
  overflow: hidden;

  &.is-expanded {
    display: block;
    overflow: visible;
    max-height: none;
  }
}

.followup-card-quote-arrow {
  margin-top: 1px;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #999;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, color 0.15s ease, background 0.15s ease;

  svg {
    width: 13px;
    height: 13px;
    display: block;
  }

  &:hover {
    color: #4a5ff3;
    background: rgba(74, 95, 243, 0.08);
  }

  &.is-expanded {
    transform: rotate(90deg);
  }
}

.followup-card-note {
  padding: 11px 19px;
  border: 1px solid #ddd;
  background: rgba(74, 95, 243, 0.1);
  color: #333;
  font-size: 16px;
  // font-weight: 600;
  line-height: 26px;
}

.followup-card-note-text {
  margin: 0;
  word-break: break-word;
  /* 默认收起 4 行 × 26px，展开时取消限高 */
  max-height: 104px;
  overflow: hidden;

  /* 仅在内容溢出、处于收起态时做底部渐隐（与用户气泡一致） */
  &.is-collapsed {
    -webkit-mask-image: linear-gradient(180deg, #000 calc(100% - 26px), rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(180deg, #000 calc(100% - 26px), rgba(0, 0, 0, 0) 100%);
  }

  &.is-expanded {
    max-height: none;
    overflow: visible;
  }
}

/* ========== Markdown 原文弹层（对齐客户端全屏代码预览） ========== */
.markdown-source-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  background: #f8f8f9;
}

.markdown-source-header {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 12px;
}

.markdown-source-header-side {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 70px;
}

.markdown-source-header-side--right {
  justify-content: flex-end;
}

.markdown-source-header-center {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.markdown-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  color: rgba(31, 35, 41, 0.7);
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;

  i {
    font-size: 14px;
  }
}

.markdown-source-badge--file {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  line-height: 26px;
  text-overflow: ellipsis;
}

.markdown-source-icon-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  color: rgba(31, 35, 41, 0.7);
  font-size: 16px;
  cursor: pointer;
  transition: color 0.15s ease, opacity 0.15s ease;

  &:hover {
    color: #1f2329;
  }
}

/* 客户端该弹层无复制按钮：默认隐藏，悬停弹层时显示，保持视觉一致 */
.markdown-source-copy-btn {
  opacity: 0;
}

.markdown-source-overlay:hover .markdown-source-copy-btn {
  opacity: 1;
}

.markdown-source-scroll {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  padding: 72px 24px 24px;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
}

.markdown-source-scroll-inner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
}

.markdown-source-card {
  display: flex;
  flex-direction: column;
  width: max-content;
  max-width: 100%;
  min-width: 850px;
  min-height: 320px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 48px rgba(31, 35, 41, 0.12),
    0 8px 16px rgba(31, 35, 41, 0.06), 0 0 0 1px rgba(31, 35, 41, 0.06);
}

.markdown-source-card-title {
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(31, 35, 41, 0.07);
  color: rgba(31, 35, 41, 0.3);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-align: center;
  user-select: none;
}

.markdown-source-code {
  display: flex;
  flex: 1;
  align-items: stretch;
  min-height: 0;
}

.markdown-source-gutter {
  position: sticky;
  left: 0;
  z-index: 1;
  flex-shrink: 0;
  min-width: 60px;
  padding: 16px 16px 16px 0;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  text-align: right;
  user-select: none;
}

.markdown-source-line-no {
  padding: 0 8px;
  color: rgba(0, 0, 0, 0.3);
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas,
    monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: nowrap;
}

.markdown-source-code-body {
  flex: 1;
  min-width: 0;
  padding: 16px;
  overflow-x: auto;
}

.markdown-source-pre {
  margin: 0;
  color: #1f2329;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas,
    monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre;
  tab-size: 2;
}

.markdown-source-fade-enter-active,
.markdown-source-fade-leave-active {
  transition: opacity 0.18s ease;
}

.markdown-source-fade-enter,
.markdown-source-fade-leave-to {
  opacity: 0;
}

.streaming-cursor {
  display: inline-block;
  width: 3px;
  height: 16px;
  background: #999;
  margin-left: 2px;
  vertical-align: middle;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  50% { opacity: 0; }
}

.tool-content {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #646a73;

  i {
    color: #3370ff;
  }
}

.tool-name {
  color: #3370ff;
  font-weight: 500;
}

.tool-result {
  margin-top: 8px;

  pre {
    background: #f6f8fa;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    overflow-x: auto;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 200px;
    overflow-y: auto;
  }
}

.center-iframe-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  margin: 4px 0;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fff;
  color: #4a6cf7;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #4a6cf7;
    background: #f0f4ff;
  }

  .el-icon-full-screen {
    font-size: 16px;
  }

  .el-icon-arrow-right {
    font-size: 12px;
    color: #c0c4cc;
  }
}

.component-loading-placeholder {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;

  .loading-text {
    color: #409eff;
    font-weight: 600;
    font-size: 14px;
  }

  .loading-icon {
    color: #409eff;
    font-size: 16px;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.image-preview-container {
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}

.image-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #2a2a2a;
  border-bottom: 1px solid #3a3a3a;
}

.image-preview-title {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 16px;
}

.image-preview-actions {
  display: flex;
  gap: 8px;
}

.preview-action-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  i {
    font-size: 16px;
  }
}

.image-preview-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;

  img {
    max-width: 100%;
    max-height: calc(90vh - 80px);
    display: block;
    object-fit: contain;
  }
}

.image-preview-fade-enter-active,
.image-preview-fade-leave-active {
  transition: opacity 0.2s;
}

.image-preview-fade-enter,
.image-preview-fade-leave-to {
  opacity: 0;
}

/* ====================================================================
   Markdown 回显样式（mateclaw 同款）
   所有后代选择器必须用 ::v-deep，否则 Vue2 scoped 不会匹配 v-html 注入元素
   ==================================================================== */
.markdown-body {
  font-size: 12px;
  line-height: 1.85;
  color: #1e293b;

  --mc-code-bg: #FAF6F1;
  --mc-code-header-bg: #EFE8DE;
  --mc-code-header-border: rgba(28, 20, 16, 0.08);
  --mc-code-lang-color: #5A6B82;
  --mc-code-copy-color: #5A6B82;
  --mc-code-copy-hover-bg: rgba(28, 20, 16, 0.06);
  --mc-code-copy-hover-color: #1C1410;
  --mc-code-text: #1C1410;
  --mc-code-line-number: rgba(28, 20, 16, 0.38);

  --mc-inline-code-bg: rgba(0, 93, 221, 0.1);
  --mc-inline-code-color: #0F2A4A;

  --mc-table-header-bg: #EEF3FB;
  --mc-table-border: #D9E1EE;

  --mc-blockquote-bg: rgba(0, 93, 221, 0.05);
  --mc-blockquote-border: #B6CDF2;
  --mc-blockquote-color: #5A6B82;

  --mc-link-color: #0049A8;
  --mc-hr-color: #D9E1EE;

  --mc-mermaid-bg: #FAFAF8;
  --mc-mermaid-border: #D9E1EE;

  --mc-thinking-bg: rgba(0, 93, 221, 0.06);
  --mc-thinking-text: #475569;
  --mc-thinking-hover: rgba(0, 93, 221, 0.1);
  --mc-thinking-icon-bg: rgba(0, 93, 221, 0.12);
  --mc-thinking-border: rgba(0, 93, 221, 0.2);

  ::v-deep {
    /* 富组件卡片统一上下间距 10px */
    .core-dashboard-card,
    .analysis-summary-card,
    .ma-card,
    .key-judgment-card,
    .mc-card,
    .key-finding-card,
    .analysis-conclusion-card,
    .section-block,
    .sw-card,
    .decision-suggestion-card {
      margin: 10px 0;
    }

    p { margin: 8px 0; font-size: 12px; }
    p:last-child { margin-bottom: 0; }

    h1 {
      margin: 24px 0 12px;
      font-weight: 600;
      line-height: 1.4;
      color: #1e293b;
      font-size: 18px;
    }
    h2 {
      margin: 20px 0 10px;
      font-weight: 600;
      line-height: 1.4;
      color: #1e293b;
      font-size: 15px;
    }
    h3 {
      margin: 16px 0 8px;
      font-weight: 600;
      line-height: 1.4;
      color: #1e293b;
      font-size: 13px;
    }
    h4, h5, h6 {
      margin: 12px 0 6px;
      font-weight: 600;
      line-height: 1.4;
      color: #1e293b;
      font-size: 12px;
    }

    code {
      font-family: 'JetBrains Mono', 'Fira Code', Consolas, monospace;
      font-size: 0.875rem;
    }
    :not(pre) > code {
      background: var(--mc-inline-code-bg);
      color: var(--mc-inline-code-color);
      padding: 2px 6px;
      border-radius: 6px;
      font-size: 0.92em;
    }

    /* ===== code-block 容器（与 mateClaw 一致） ===== */
    .code-block {
      margin: 14px 0;
      border-radius: 12px;
      overflow: hidden;
      background: var(--mc-code-bg);
      border: 1px solid var(--mc-code-header-border);
    }
    .code-block__header {
      display: flex !important;
      align-items: center;
      justify-content: space-between;
      padding: 0 14px !important;
      margin: 0 !important;
      height: 38px !important;
      min-height: 38px !important;
      max-height: 38px !important;
      line-height: 1 !important;
      font-size: 12px !important;
      background: var(--mc-code-header-bg);
      border-bottom: 1px solid var(--mc-code-header-border);
      box-sizing: border-box;
      overflow: hidden;
    }
    .code-block__header svg {
      width: 14px !important;
      height: 14px !important;
      flex-shrink: 0;
      display: inline-block;
      vertical-align: middle;
    }
    .code-block__header > * {
      flex-shrink: 0;
      min-width: 0;
    }
    .code-block__lang {
      font-size: 12px;
      color: var(--mc-code-lang-color);
      font-weight: 500;
      letter-spacing: 0.02em;
    }
    .code-block__lines {
      display: none;
    }
    .code-block__copy {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: var(--mc-code-copy-color);
      font-size: 12px;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .code-block__copy:hover {
      background: var(--mc-code-copy-hover-bg);
      color: var(--mc-code-copy-hover-color);
    }
    .code-block pre {
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
    }
    .code-block pre code,
    .code-block pre code.hljs {
      background: transparent !important;
      padding: 0 !important;
      color: var(--mc-code-text);
      display: block;
      line-height: 1.55;
    }
    .code-block ol.hljs-lines {
      counter-reset: ln;
      padding: 12px 0;
      margin: 0;
      list-style: none;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace;
    }
    .code-block ol.hljs-lines > li {
      counter-increment: ln;
      display: block;
      padding: 0 16px 0 4em;
      position: relative;
      white-space: pre;
      min-height: 1.55em;
      line-height: 1.55;
    }
    .code-block ol.hljs-lines > li::before {
      content: counter(ln);
      position: absolute;
      left: 0;
      width: 3em;
      text-align: right;
      color: var(--mc-code-line-number);
      user-select: none;
      font-size: 0.85em;
      font-variant-numeric: tabular-nums;
    }
    details.code-block--collapsible > summary {
      display: block;
      list-style: none;
      cursor: pointer;
    }
    details.code-block--collapsible > summary::-webkit-details-marker,
    details.code-block--collapsible > summary::marker {
      display: none;
      content: '';
    }
    details.code-block--collapsible:not([open]) .code-block__lines {
      display: inline-block;
      font-size: 11px;
      color: var(--mc-code-lang-color);
      margin: 0 12px;
      padding: 2px 8px;
      border-radius: 4px;
      background: var(--mc-code-copy-hover-bg);
      user-select: none;
    }
    details.code-block--collapsible:not([open]) > pre {
      display: none;
    }

    /* 非 code-block 包裹的 pre（向后兼容） */
    pre {
      background: var(--mc-code-bg);
      border-radius: 12px;
      padding: 16px;
      overflow-x: auto;
      margin: 14px 0;
    }
    pre code,
    pre code.hljs {
      background: none !important;
      color: var(--mc-code-text);
      padding: 0 !important;
      border-radius: 0;
      font-size: 0.875rem;
      display: block;
      overflow-x: auto;
      line-height: 1.7;
    }

    ul, ol {
      padding-left: 1.5rem;
      margin: 8px 0;
      font-size: 12px;
    }
    ul { list-style-type: disc; }
    ol { list-style-type: decimal; }
    li {
      margin: 6px 0;
      line-height: 2.0;
      font-size: 12px;
      /* 全局 reset（styles/normalize.scss）给 li 显式写了 list-style: none，
         会覆盖 ul/ol 继承下来的 list-style-type，这里必须显式恢复 */
      list-style: inherit;
      list-style-position: outside;
    }
    ul > li::marker { color: #475569; }
    ol > li::marker { color: #475569; }
    li > ul,
    li > ol {
      margin: 4px 0;
    }

    /* ---- task list (marked GFM) ---- */
    li.task-list-item {
      list-style: none;
      padding-left: 0;
      display: flex;
      align-items: flex-start;
      line-height: 1.85;
      font-size: 12px;
    }
    li.task-list-item > input.task-list-item-checkbox {
      all: unset !important;
      -webkit-appearance: checkbox !important;
      appearance: checkbox !important;
      width: 14px !important;
      height: 14px !important;
      margin: 3px 8px 0 0 !important;
      padding: 0 !important;
      border: none !important;
      border-radius: 0 !important;
      background: initial !important;
      box-shadow: none !important;
      vertical-align: middle;
      accent-color: #67C23A;
      cursor: default;
      flex-shrink: 0;
      display: inline-block;
    }
    /* 选中样式 (现代浏览器 :has() + 旧浏览器 JS 兜底类 .task-list-item--checked) */
    li.task-list-item:has(input:checked) {
      color: #999;
    }
    li.task-list-item:has(input:checked) > :not(input) {
      text-decoration: line-through;
      color: #999;
    }
    li.task-list-item.task-list-item--checked {
      color: #999;
    }
    li.task-list-item.task-list-item--checked > :not(input) {
      text-decoration: line-through;
      color: #999;
    }

    table:not(.el-table__header):not(.el-table__body):not(.el-table__footer) {
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      table-layout: auto;
      max-width: 100%;
      margin: 12px 0;
      font-size: 13px;
      line-height: 1.5;
      border-radius: 8px;
      border: 1px solid #e7e8eb;
      background: #ffffff;
      overflow: hidden;
    }
    th:not(.el-table__cell),
    td:not(.el-table__cell) {
      padding: 10px 14px !important;
      text-align: left;
      font-size: 13px;
      border: none !important;
      border-right: 1px solid #e7e8eb !important;
      border-bottom: 1px solid #e7e8eb !important;

      &:last-child {
        border-right: none !important;
      }
    }
    th:not(.el-table__cell) {
      background: var(--mc-table-header-bg) !important;
      font-weight: 600 !important;
      color: #1f2329;
      white-space: nowrap;
    }
    /* 圆角：四角单元格跟随外框 8px 圆角 */
    thead tr:first-child th:not(.el-table__cell):first-child {
      border-top-left-radius: 8px;
    }
    thead tr:first-child th:not(.el-table__cell):last-child {
      border-top-right-radius: 8px;
    }
    tbody tr:last-child td:not(.el-table__cell):first-child {
      border-bottom-left-radius: 8px;
    }
    tbody tr:last-child td:not(.el-table__cell):last-child {
      border-bottom-right-radius: 8px;
    }
    tbody tr:last-child td:not(.el-table__cell) {
      border-bottom: none !important;
    }
    tr:not(.el-table__row):hover td:not(.el-table__cell) {
      background: rgba(74, 95, 243, 0.04);
    }
    /* 可选：带 <!--md-table:full--> 标记的表格铺满消息宽度（默认无该标记，其它回答不受影响） */
    .md-table-full {
      width: 100%;
      overflow-x: auto;

      table:not(.el-table__header):not(.el-table__body):not(.el-table__footer) {
        display: table;
        table-layout: auto;
      }
      th:not(.el-table__cell),
      td:not(.el-table__cell) {
        word-break: break-word;
      }
    }

    /* md-echarts-block：markdown fence → ECharts 占位容器 */
    .md-echarts-block {
      margin: 12px 0;
      border-radius: 8px;
      overflow: hidden;
      background: transparent;
      border: 1px solid #e7e8eb;
    }
    .md-echarts-block__body {
      width: 100%;
      min-height: 280px;
    }
    .echarts-error {
      color: #e5484d;
      padding: 12px;
      font-size: 13px;
    }

    blockquote {
      border-left: 4px solid var(--mc-blockquote-border);
      margin: 12px 0;
      padding: 8px 16px;
      color: var(--mc-blockquote-color);
      background: var(--mc-blockquote-bg);
      border-radius: 0 8px 8px 0;
    }
    blockquote p { margin: 4px 0; }

    hr {
      border: none;
      border-top: 1px solid var(--mc-hr-color);
      margin: 16px 0;
    }

    a {
      color: var(--mc-link-color);
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }
    a:hover {
      border-bottom-color: var(--mc-link-color);
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 10px 0;
    }

    del,
    s {
      text-decoration: line-through;
      opacity: 0.7;
    }

    strong,
    b {
      font-weight: 600;
      color: #1e293b;
    }

    .thinking-content {
      padding: 10px 14px 6px;
      color: var(--mc-thinking-text);
      font-size: 13px;
      line-height: 1.65;
      border-left: 2px solid var(--mc-thinking-border);
      margin-left: 12px;
      margin-top: 8px;
    }
    .thinking-content > * {
      max-width: 100%;
    }
  }
}
</style>

<style lang="scss">
/* 非 scoped：用于 richHtml 渲染的 @数字人 标签样式 */
.mention-chip-inline {
  display: inline-block;
  background: rgba(74, 95, 243, 0.1);
  color: #4A5FF3;
  border-radius: 4px;
  padding: 1px 7px;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.7;
  margin-right: 2px;
}

/* 非 scoped：用于聊天气泡中 [附件] 标记渲染的超链接样式 */
.chat-attachment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4A5FF3;
  text-decoration: none;
  font-size: 13px;
  border: 1px solid #D6DEF5;
  border-radius: 6px;
  padding: 2px 8px;
  background: rgba(74, 95, 243, 0.04);
  transition: background 0.15s, border-color 0.15s;
  vertical-align: middle;
  margin: 1px 2px;
  max-width: 280px;

  &:hover {
    background: rgba(74, 95, 243, 0.1);
    border-color: #4A5FF3;
    text-decoration: none;
  }

  i {
    font-size: 14px;
    flex-shrink: 0;
  }
}

</style>
